<template>
  <div class="planes-manager">
    
    <!-- Header y Controles -->
    <div class="manager-header">
      <div class="row align-items-center">
        <div class="col-md-6">
          <h4 class="manager-title">
            <i class="nc-icon nc-credit-card mr-2"></i>
            Gestión de Planes
          </h4>
          <small class="text-muted">
            Consultar, crear, editar y gestionar los planes completos del sistema
          </small>
        </div>
        <div class="col-md-6 text-right">
          <button 
            class="btn btn-info btn-sm mr-2" 
            @click="exportarExcel"
            :disabled="loading.tabla || planes.length === 0 || loading.exportar"
            title="Exportar a Excel"
          >
            <span v-if="loading.exportar" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="nc-icon nc-paper mr-1"></i>
            <span class="d-none d-sm-inline">Excel</span>
          </button>
          <button 
            class="btn btn-primary btn-sm mr-2" 
            @click="abrirModalCrear"
            :disabled="loading.crear || loading.tabla"
          >
            <span v-if="loading.crear" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="nc-icon nc-simple-add mr-1"></i>
            Nuevo Plan
          </button>
          <button 
            class="btn btn-outline-secondary btn-sm" 
            @click="refreshPlanes"
            :disabled="loading.tabla || loading.refrescar"
          >
            <span v-if="loading.refrescar" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="nc-icon nc-refresh-02 mr-1"></i>
            <span class="d-none d-sm-inline">Refrescar</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Información de Categoría Preseleccionada -->
    <div v-if="categoriaPreseleccionada" class="categoria-preseleccionada mb-4">
      <div class="card">
        <div class="card-body py-3">
          <div class="row align-items-center">
            <div class="col-md-8">
              <div class="categoria-info">
                <h6 class="mb-1">
                  <i class="nc-icon nc-check-2 text-success mr-1"></i>
                  Navegación desde SubFamilia
                </h6>
                <small class="text-muted">
                  <span v-if="categoriaPreseleccionada.familiaNombre">
                    <strong>Familia:</strong> {{ categoriaPreseleccionada.familiaNombre }} | 
                  </span>
                  <strong>SubFamilia:</strong> {{ categoriaPreseleccionada.subfamiliaNombre }}
                  <span class="badge ml-2" :class="categoriaPreseleccionada.familiaNombre ? 'badge-primary' : 'badge-info'">
                    {{ categoriaPreseleccionada.familiaNombre ? 'Filtros aplicados automáticamente' : 'Filtrado solo por SubFamilia' }}
                  </span>
                </small>
              </div>
            </div>
            <div class="col-md-4 text-right">
              <button 
                class="btn btn-outline-secondary btn-sm"
                @click="limpiarNavegacion"
                title="Limpiar filtros y ver todos los planes"
              >
                <i class="nc-icon nc-simple-remove mr-1"></i>
                Ver Todos los Planes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtros Avanzados -->
    <div class="filters-section mb-4">
      <div class="card">
        <div class="card-header">
          <div class="row align-items-center">
            <div class="col">
              <h6 class="card-title mb-0">
                <i class="nc-icon nc-zoom-split mr-2"></i>
                Filtros de Búsqueda
              </h6>
            </div>
            <div class="col-auto">
              <button 
                class="btn btn-outline-secondary btn-sm"
                @click="limpiarFiltros"
                :disabled="loading.tabla"
              >
                <i class="nc-icon nc-simple-remove mr-1"></i>
                Limpiar
              </button>
            </div>
          </div>
        </div>
        <div class="card-body">
          <!-- Primera fila de filtros -->
          <div class="row mb-3">
            <div class="col-md-4">
              <div class="form-group mb-0">
                <label class="form-label">Buscar Plan</label>
                <input 
                  type="text" 
                  class="form-control form-control-sm"
                  v-model="filtros.descripcion"
                  @input="debounceFilter"
                  placeholder="Descripción del plan..."
                >
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group mb-0">
                <label class="form-label">Familia</label>
                <select 
                  class="form-control form-control-sm"
                  v-model="filtros.idFamilia"
                  @change="onFamiliaChange"
                >
                  <option value="">Todas las familias</option>
                  <option 
                    v-for="familia in catalogos.familias" 
                    :key="familia.id"
                    :value="familia.id"
                  >
                    {{ familia.nombre }} (ID: {{ familia.id }})
                  </option>
                </select>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group mb-0">
                <label class="form-label">SubFamilia</label>
                <select 
                  class="form-control form-control-sm"
                  v-model="filtros.idSubFamilia"
                  @change="aplicarFiltros"
                  :disabled="!filtros.idFamilia"
                >
                  <option value="">Todas las subfamilias</option>
                  <option 
                    v-for="subfamilia in subfamiliasDisponibles" 
                    :key="subfamilia.id"
                    :value="subfamilia.id"
                  >
                    {{ subfamilia.nombre }} (ID: {{ subfamilia.id }})
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Segunda fila de filtros -->
          <div class="row mb-3">
            <div class="col-md-3">
              <div class="form-group mb-0">
                <label class="form-label">Habilitado para Venta</label>
                <select 
                  class="form-control form-control-sm"
                  v-model="filtros.habilitarVenta"
                  @change="aplicarFiltros"
                >
                  <option value="">Todos</option>
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group mb-0">
                <label class="form-label">MultiSede</label>
                <select 
                  class="form-control form-control-sm"
                  v-model="filtros.multiSede"
                  @change="aplicarFiltros"
                >
                  <option value="">Todos</option>
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group mb-0">
                <label class="form-label">Estado</label>
                <select 
                  class="form-control form-control-sm"
                  v-model="filtros.activo"
                  @change="aplicarFiltros"
                >
                  <option value="">Todos</option>
                  <option value="true">Activos</option>
                  <option value="false">Inactivos</option>
                </select>
              </div>
            </div>

          </div>

          <!-- Tercera fila de filtros - Rango de precios -->
          <div class="row">
            <div class="col-md-3">
              <div class="form-group mb-0">
                <label class="form-label">Precio Mínimo</label>
                <input 
                  type="number" 
                  class="form-control form-control-sm"
                  v-model.number="filtros.valorMin"
                  @input="debounceFilter"
                  placeholder="Ej: 1000"
                  min="0"
                  step="100"
                >
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group mb-0">
                <label class="form-label">Precio Máximo</label>
                <input 
                  type="number" 
                  class="form-control form-control-sm"
                  v-model.number="filtros.valorMax"
                  @input="debounceFilter"
                  placeholder="Ej: 50000"
                  min="0"
                  step="100"
                >
              </div>
            </div>
            <div class="col-md-6">
              <div class="filtros-activos" v-if="tienesFiltrosActivos">
                <label class="form-label">Filtros Activos</label>
                <div class="d-flex flex-wrap">
                  <span v-for="filtro in filtrosActivosDisplay" :key="filtro.key" class="badge badge-primary mr-1 mb-1">
                    {{ filtro.label }}: {{ filtro.value }}
                    <i class="nc-icon nc-simple-remove ml-1" @click="removerFiltro(filtro.key)" style="cursor: pointer;"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla de Planes -->
    <div class="table-section">
      <div class="card">
        <div class="card-header">
          <div class="row align-items-center">
            <div class="col">
              <h6 class="card-title mb-0">
                Lista de Planes
                <span class="badge badge-secondary ml-2" v-if="!loading.tabla">
                  {{ pagination.totalRecords }} registros
                </span>
              </h6>
            </div>
            <div class="col-auto">
              <div class="d-flex align-items-center">
                <small class="text-muted mr-2">Mostrar:</small>
                <select 
                  class="form-control form-control-sm"
                  v-model="pagination.limit"
                  @change="cambiarLimite"
                  style="width: auto;"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card-body p-0">
          <!-- Loading -->
          <div v-if="loading.tabla" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Cargando planes...</span>
            </div>
            <p class="mt-3 text-muted">Cargando planes...</p>
          </div>

          <!-- Tabla -->
          <div v-else class="table-responsive">
            <table class="table table-hover mb-0">
              <thead class="thead-light">
                <tr>
                  <th>
                    <button class="btn-sort" @click="sortBy('id')">
                      ID 
                      <i class="nc-icon nc-minimal-up" v-if="ordenamiento.campo === 'id' && ordenamiento.direccion === 'asc'"></i>
                      <i class="nc-icon nc-minimal-down" v-if="ordenamiento.campo === 'id' && ordenamiento.direccion === 'desc'"></i>
                    </button>
                  </th>
                  <th>
                    <button class="btn-sort" @click="sortBy('descripcion')">
                      Plan
                      <i class="nc-icon nc-minimal-up" v-if="ordenamiento.campo === 'descripcion' && ordenamiento.direccion === 'asc'"></i>
                      <i class="nc-icon nc-minimal-down" v-if="ordenamiento.campo === 'descripcion' && ordenamiento.direccion === 'desc'"></i>
                    </button>
                  </th>
                  <th>
                    <button class="btn-sort" @click="sortBy('valor')">
                      Precio
                      <i class="nc-icon nc-minimal-up" v-if="ordenamiento.campo === 'valor' && ordenamiento.direccion === 'asc'"></i>
                      <i class="nc-icon nc-minimal-down" v-if="ordenamiento.campo === 'valor' && ordenamiento.direccion === 'desc'"></i>
                    </button>
                  </th>
                  <th>Categoría</th>
                  <th>Duración</th>
                  <th>Estado</th>
                  <th>Características</th>
                  <th width="120">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="plan in planes" :key="plan._id">
                  <td>
                    <span class="badge badge-outline-warning">{{ plan.id }}</span>
                  </td>
                  <td>
                    <div class="plan-info">
                      <strong 
                        class="plan-nombre-clickeable"
                        @click="editarPlan(plan)"
                        :title="'Editar plan: ' + plan.descripcion"
                      >
                        {{ plan.descripcion }}
                      </strong>
                      <br>
                      <small class="text-muted">
                        <i class="nc-icon nc-calendar-60 mr-1"></i>
                        {{ plan.fechaCreacionFormatted }}
                      </small>
                    </div>
                  </td>
                  <td>
                    <div class="precio-info">
                      <strong class="text-success">${{ formatearPrecioExacto(plan.valor) }}</strong>
                      <br>
                      <small v-if="plan.indicadorMatricula" class="text-muted">
                        + Matrícula: ${{ formatearPrecioExacto(plan.valorMatricula) }}
                      </small>
                    </div>
                  </td>
                  <td>
                    <div class="categoria-info">
                      <strong>{{ plan.nombreFamilia }}</strong>
                      <br>
                      <small class="text-muted">{{ plan.nombreSubFamilia }}</small>
                    </div>
                  </td>
                  <td>
                    <span class="badge badge-info">
                      {{ plan.duracion }} {{ plan.unidadDuracionPlan && plan.unidadDuracionPlan.glosa ? plan.unidadDuracionPlan.glosa : 'N/A' }}
                    </span>
                  </td>
                  <td>
                    <div class="estado-info">
                      <span 
                        class="badge"
                        :class="plan.activo ? 'badge-success' : 'badge-secondary'"
                      >
                        {{ plan.activo ? 'Activo' : 'Inactivo' }}
                      </span>
                      <br>
                      <small v-if="plan.habilitarVenta" class="text-success">
                        <i class="nc-icon nc-check-2"></i> Venta habilitada
                      </small>
                      <small v-else class="text-muted">
                        <i class="nc-icon nc-simple-remove"></i> Venta deshabilitada
                      </small>
                    </div>
                  </td>
                  <td>
                    <div class="caracteristicas-info">
                      <div class="d-flex flex-wrap">
                        <span v-if="plan.indicadorMatricula" class="badge badge-sm badge-primary mr-1 mb-1">Matrícula</span>
                        <span v-if="plan.indicadorUsoSesiones" class="badge badge-sm badge-info mr-1 mb-1">Sesiones</span>
                        <span v-if="plan.multiSede" class="badge badge-sm badge-warning mr-1 mb-1">MultiSede</span>
                        <span v-if="plan.indicadorPermiteRenovar" class="badge badge-sm badge-success mr-1 mb-1">Renovable</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="btn-group btn-group-sm">
                      <button 
                        class="btn btn-outline-info"
                        @click="verDetalle(plan)"
                        :disabled="loading.verDetalle === plan.id"
                        :title="'Ver detalles de ' + plan.descripcion"
                      >
                        <span v-if="loading.verDetalle === plan.id" class="spinner-border spinner-border-sm"></span>
                        <i v-else class="nc-icon nc-zoom-split"></i>
                      </button>
                      <button 
                        class="btn btn-outline-primary"
                        @click="editarPlan(plan)"
                        :disabled="loading.editar === plan.id"
                        :title="'Editar ' + plan.descripcion"
                      >
                        <span v-if="loading.editar === plan.id" class="spinner-border spinner-border-sm"></span>
                        <i v-else class="nc-icon nc-ruler-pencil"></i>
                      </button>
                      <button 
                        class="btn btn-outline-danger"
                        @click="confirmarEliminar(plan)"
                        :title="plan.activo ? 'Eliminar (Etapa 1): ' + plan.descripcion : 'Eliminar Permanentemente (Etapa 2): ' + plan.descripcion"
                        :disabled="loading.eliminar === plan.id"
                      >
                        <span v-if="loading.eliminar === plan.id" class="spinner-border spinner-border-sm"></span>
                        <i v-else class="nc-icon nc-simple-remove"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Sin Resultados -->
          <div v-if="!loading.tabla && planes.length === 0" class="empty-state text-center py-5">
            <i class="nc-icon nc-credit-card empty-icon"></i>
            <h5>No hay planes</h5>
            <p class="text-muted" v-if="categoriaPreseleccionada">
              <span v-if="categoriaPreseleccionada.familiaNombre">
                No se encontraron planes en la subfamilia "{{ categoriaPreseleccionada.subfamiliaNombre }}" 
                de la familia "{{ categoriaPreseleccionada.familiaNombre }}".
              </span>
              <span v-else>
                No se encontraron planes en la subfamilia "{{ categoriaPreseleccionada.subfamiliaNombre }}".
              </span>
            </p>
            <p class="text-muted" v-else>
              No se encontraron planes con los filtros aplicados.
            </p>
            <div class="mt-3">
              <button v-if="categoriaPreseleccionada" class="btn btn-success mr-2" @click="abrirModalCrear">
                <i class="nc-icon nc-simple-add mr-1"></i>
                Crear primer plan en esta categoría
              </button>
              <button class="btn btn-primary" @click="limpiarFiltros">
                <i class="nc-icon nc-simple-remove mr-1"></i>
                {{ categoriaPreseleccionada ? 'Ver todos los planes' : 'Limpiar filtros' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Paginación -->
    <div v-if="pagination.totalPages > 1" class="pagination-section mt-4">
      <nav>
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: !pagination.hasPrev }">
            <button 
              class="page-link" 
              @click="cambiarPagina(pagination.prevPage)"
              :disabled="!pagination.hasPrev"
            >
              <i class="nc-icon nc-minimal-left"></i>
            </button>
          </li>
          
          <li 
            v-for="pagina in paginasVisibles" 
            :key="pagina"
            class="page-item" 
            :class="{ active: pagina === pagination.page }"
          >
            <button class="page-link" @click="cambiarPagina(pagina)">
              {{ pagina }}
            </button>
          </li>
          
          <li class="page-item" :class="{ disabled: !pagination.hasNext }">
            <button 
              class="page-link" 
              @click="cambiarPagina(pagination.nextPage)"
              :disabled="!pagination.hasNext"
            >
              <i class="nc-icon nc-minimal-right"></i>
            </button>
          </li>
        </ul>
      </nav>
      
      <div class="pagination-info text-center mt-2">
        <small class="text-muted">
          Mostrando {{ (pagination.page - 1) * pagination.limit + 1 }} a 
          {{ Math.min(pagination.page * pagination.limit, pagination.totalRecords) }} 
          de {{ pagination.totalRecords }} registros
        </small>
      </div>
    </div>

    <!-- Modal Crear/Editar Plan -->
    <div class="modal fade" ref="modalCrearPlan" tabindex="-1" data-backdrop="static">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title mb-0">
              <i class="nc-icon nc-credit-card mr-2"></i>
              {{ modoEdicion ? 'Editar Plan' : 'Nuevo Plan' }}
            </h6>
            <button type="button" class="close" @click="cerrarModalCreacion">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          
          <div class="modal-body p-0">
            <!-- Navegación por Pestañas -->
            <div class="sticky-tabs-container">
              <ul class="nav nav-tabs nav-justified mb-0">
              <li class="nav-item">
                <a 
                  class="nav-link" 
                  :class="{ active: activeTabCreacion === 'basico' }"
                  @click="activeTabCreacion = 'basico'"
                  href="#"
                >
                  <i class="nc-icon nc-paper mr-1"></i>
                  Básico
                </a>
              </li>
              <li class="nav-item">
                <a 
                  class="nav-link" 
                  :class="{ active: activeTabCreacion === 'duracion' }"
                  @click="activeTabCreacion = 'duracion'"
                  href="#"
                >
                  <i class="nc-icon nc-time-alarm mr-1"></i>
                  Duración
                </a>
              </li>
              <li class="nav-item">
                <a 
                  class="nav-link" 
                  :class="{ active: activeTabCreacion === 'configuracion' }"
                  @click="activeTabCreacion = 'configuracion'"
                  href="#"
                >
                  <i class="nc-icon nc-settings-gear-65 mr-1"></i>
                  Configuración
                </a>
              </li>
              <li class="nav-item">
                <a 
                  class="nav-link" 
                  :class="{ active: activeTabCreacion === 'turnos' }"
                  @click="activeTabCreacion = 'turnos'"
                  href="#"
                >
                  <i class="nc-icon nc-calendar-60 mr-1"></i>
                  Turnos
                </a>
              </li>
              <li class="nav-item">
                <a 
                  class="nav-link" 
                  :class="{ active: activeTabCreacion === 'adicional' }"
                  @click="activeTabCreacion = 'adicional'"
                  href="#"
                >
                  <i class="nc-icon nc-single-02 mr-1"></i>
                  Adicional
                </a>
              </li>
              <li class="nav-item">
                <a 
                  class="nav-link" 
                  :class="{ active: activeTabCreacion === 'sucursales' }"
                  @click="activeTabCreacion = 'sucursales'"
                  href="#"
                >
                  <i class="nc-icon nc-pin-3 mr-1"></i>
                  Sucursales
                </a>
              </li>
            </ul>
            </div>

            <!-- Contenido de las Pestañas -->
            <div class="tab-content-scrollable">
              <div class="tab-content-inner">
              
              <!-- PESTAÑA BÁSICO -->
              <div v-if="activeTabCreacion === 'basico'" class="tab-pane active">
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="form-label">Familia <span class="text-danger">*</span></label>
                      <select 
                        class="form-control"
                        id="familia"
                        v-model="formPlan.familia"
                        @change="onFamiliaFormChange"
                        :disabled="loading.guardar"
                      >
                        <option value="">-- Selecciona una familia --</option>
                        <option 
                          v-for="familia in catalogos.familias" 
                          :key="familia._id"
                          :value="familia._id"
                        >
                          {{ familia.nombre }} (ID: {{ familia.id }})
                        </option>
                      </select>
                    </div>
                  </div>
                  
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="form-label">SubFamilia <span class="text-danger">*</span></label>
                      <select 
                        class="form-control"
                        id="subfamilia"
                        v-model="formPlan.subfamilia"
                        :disabled="!formPlan.familia || loading.guardar"
                      >
                        <option value="">-- Selecciona una subfamilia --</option>
                        <option 
                          v-for="subfamilia in subfamiliasFormulario" 
                          :key="subfamilia._id"
                          :value="subfamilia._id"
                        >
                          {{ subfamilia.nombre }} (ID: {{ subfamilia.id }})
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label for="descripcionPlan" class="form-label">Descripción del Plan <span class="text-danger">*</span></label>
                  <input 
                    type="text" 
                    class="form-control"
                    id="descripcionPlan"
                    v-model="formPlan.descripcion"
                    placeholder="Ej: Plan Mensual Premium, Trimestral + Beneficios..."
                    maxlength="200"
                    :disabled="loading.guardar"
                  >
                  <small class="form-text text-muted">Máximo 200 caracteres. Debe ser único por familia/subfamilia.</small>
                </div>

                <div class="row">
                  <div class="col-md-4">
                    <div class="form-group">
                      <label class="form-label">Valor del Plan <span class="text-danger">*</span></label>
                      <input 
                        type="number" 
                        class="form-control"
                        id="valor"
                        v-model.number="formPlan.valor"
                        placeholder="0 (se permite valor cero)"
                        min="0"
                        step="0.01"
                        :disabled="loading.guardar"
                      >
                      <small class="form-text text-success">
                        <i class="nc-icon nc-bulb-63 mr-1"></i>
                        Se acepta valor $0 para planes gratuitos, becas, regalos o convenios especiales
                      </small>
                    </div>
                  </div>
                  
                  <div class="col-md-4">
                    <div class="form-group">
                      <label class="form-label">Descuento (%)</label>
                      <input 
                        type="number" 
                        class="form-control"
                        v-model.number="formPlan.porcentajeDescuento"
                        placeholder="0"
                        min="0"
                        max="100"
                        step="0.1"
                        :disabled="loading.guardar"
                      >
                    </div>
                  </div>
                  
                  <div class="col-md-4">
                    <div class="form-group">
                      <label class="form-label">Tipo IVA</label>
                      <select 
                        class="form-control"
                        v-model="formPlan.tipoIVA"
                        :disabled="loading.guardar"
                      >
                        <option value="AFECTO">AFECTO</option>
                        <option value="EXENTO">EXENTO</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="custom-control custom-checkbox">
                      <input 
                        type="checkbox" 
                        class="custom-control-input" 
                        id="indicadorMatricula"
                        v-model="formPlan.indicadorMatricula"
                        :disabled="loading.guardar"
                      >
                      <label class="custom-control-label" for="indicadorMatricula">
                        Requiere Matrícula
                      </label>
                    </div>
                    
                    <div v-if="formPlan.indicadorMatricula" class="form-group mt-2">
                      <label class="form-label">Valor Matrícula</label>
                      <input 
                        type="number" 
                        class="form-control"
                        v-model.number="formPlan.valorMatricula"
                        placeholder="0 (se permite valor cero)"
                        min="0"
                        step="0.01"
                        :disabled="loading.guardar"
                      >
                      <small class="form-text text-info">
                        Valor de la matrícula (se acepta $0 para matrículas gratuitas)
                      </small>
                    </div>
                  </div>
                  
                  <div class="col-md-6">
                    <div class="custom-control custom-checkbox">
                      <input 
                        type="checkbox" 
                        class="custom-control-input" 
                        id="indicadorFactura"
                        v-model="formPlan.indicadorFactura"
                        :disabled="loading.guardar"
                      >
                      <label class="custom-control-label" for="indicadorFactura">
                        Genera Factura Automática
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- PESTAÑA DURACIÓN -->
              <div v-if="activeTabCreacion === 'duracion'" class="tab-pane active">
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="form-label">Duración <span class="text-danger">*</span></label>
                      <input 
                        type="number" 
                        class="form-control"
                        id="duracion"
                        v-model.number="formPlan.duracion"
                        placeholder="1"
                        min="1"
                        :disabled="loading.guardar"
                      >
                    </div>
                  </div>
                  
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="form-label">Unidad de Duración <span class="text-danger">*</span></label>
                      <select 
                        class="form-control"
                        id="unidadDuracionPlan"
                        v-model="formPlan.unidadDuracionPlan"
                        :disabled="loading.guardar"
                      >
                        <option value="">-- Selecciona la unidad --</option>
                        <option 
                          v-for="unidad in catalogosCreacion.unidadesDuracion" 
                          :key="unidad._id"
                          :value="unidad._id"
                        >
                          {{ unidad.glosa }} - {{ unidad.descripcion }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="custom-control custom-checkbox">
                      <input 
                        type="checkbox" 
                        class="custom-control-input" 
                        id="indicadorMesCalendario"
                        v-model="formPlan.indicadorMesCalendario"
                        :disabled="loading.guardar"
                      >
                      <label class="custom-control-label" for="indicadorMesCalendario">
                        Usar Mes Calendario
                      </label>
                    </div>
                    
                    <div v-if="!formPlan.indicadorMesCalendario" class="form-group mt-2">
                      <label class="form-label">Días por Mes (No Calendario)</label>
                      <input 
                        type="number" 
                        class="form-control"
                        v-model.number="formPlan.diasMesNoCalendario"
                        placeholder="30"
                        min="1"
                        :disabled="loading.guardar"
                      >
                    </div>
                  </div>
                  
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="form-label">Días Permitidos para Congelar</label>
                      <input 
                        type="number" 
                        class="form-control"
                        v-model.number="formPlan.diasPermitidoCongelar"
                        placeholder="5"
                        min="0"
                        :disabled="loading.guardar"
                      >
                    </div>
                  </div>
                </div>

                <h6 class="mt-4 mb-3">Vigencia Temporal</h6>
                <div class="custom-control custom-checkbox mb-3">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="indicadorVigenciaTemporal"
                    v-model="formPlan.indicadorVigenciaTemporal"
                    :disabled="loading.guardar"
                  >
                  <label class="custom-control-label" for="indicadorVigenciaTemporal">
                    Tiene Vigencia Temporal
                  </label>
                </div>
                
                <div v-if="formPlan.indicadorVigenciaTemporal" class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="form-label">Fecha Inicio Vigencia <span class="text-danger">*</span></label>
                      <input 
                        type="date" 
                        class="form-control"
                        id="fechaInicioVigencia"
                        v-model="formPlan.fechaInicioVigencia"
                        :disabled="loading.guardar"
                      >
                      <small class="form-text text-muted">
                        Fecha desde la cual el plan estará disponible para venta
                      </small>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="form-label">Fecha Término Vigencia <span class="text-danger">*</span></label>
                      <input 
                        type="date" 
                        class="form-control"
                        id="fechaTerminoVigencia"
                        v-model="formPlan.fechaTerminoVigencia"
                        :disabled="loading.guardar"
                      >
                      <small class="form-text text-muted">
                        Fecha hasta la cual el plan estará disponible para venta
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- PESTAÑA CONFIGURACIÓN -->
              <div v-if="activeTabCreacion === 'configuracion'" class="tab-pane active">
                <h6 class="mb-3">Sesiones</h6>
                <div class="row">
                  <div class="col-md-4">
                    <div class="custom-control custom-checkbox">
                      <input 
                        type="checkbox" 
                        class="custom-control-input" 
                        id="indicadorUsoSesiones"
                        v-model="formPlan.indicadorUsoSesiones"
                        :disabled="loading.guardar"
                      >
                      <label class="custom-control-label" for="indicadorUsoSesiones">
                        Usa Sesiones Limitadas
                      </label>
                    </div>
                  </div>
                  
                  <div v-if="formPlan.indicadorUsoSesiones" class="col-md-4">
                    <div class="form-group">
                      <label class="form-label">Límite de Sesiones</label>
                      <input 
                        type="number" 
                        class="form-control"
                        v-model.number="formPlan.sesionesLimite"
                        placeholder="10"
                        min="1"
                        :disabled="loading.guardar"
                      >
                    </div>
                  </div>
                  
                  <div v-if="formPlan.indicadorUsoSesiones" class="col-md-4">
                    <div class="custom-control custom-checkbox mt-4">
                      <input 
                        type="checkbox" 
                        class="custom-control-input" 
                        id="descuentoSesionesXDia"
                        v-model="formPlan.descuentoSesionesXDia"
                        :disabled="loading.guardar"
                      >
                      <label class="custom-control-label" for="descuentoSesionesXDia">
                        Descontar Sesiones por Día
                      </label>
                    </div>
                  </div>
                </div>

                <h6 class="mt-4 mb-3">Multi-Sede</h6>
                <div class="custom-control custom-checkbox mb-3">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="multiSede"
                    v-model="formPlan.multiSede"
                    :disabled="loading.guardar"
                  >
                  <label class="custom-control-label" for="multiSede">
                    Permitir Acceso a Múltiples Sedes
                  </label>
                </div>

                <div v-if="formPlan.multiSede" class="mt-3">
                  <div class="alert alert-info">
                    <i class="nc-icon nc-bulb-63 mr-2"></i>
                    <strong>Nota:</strong> Si este campo está seleccionado, el socio podrá acceder al modo Multi-Sede, es decir, que la persona podrá acceder a todas las sucursales del gimnasio. Esto es diferente a la pestaña "Sucursales" que permite al administrador seleccionar en qué sucursales este plan se va a poder vender.
                  </div>
                  
                  <button 
                    type="button" 
                    class="btn btn-outline-primary btn-sm"
                    @click="activeTabCreacion = 'sucursales'"
                  >
                    <i class="nc-icon nc-pin-3 mr-1"></i>
                    Ir a Configurar Sucursales
                  </button>
                </div>

                <h6 class="mt-4 mb-3">Renovación</h6>
                <div class="row">
                  <div class="col-md-4">
                    <div class="custom-control custom-checkbox">
                      <input 
                        type="checkbox" 
                        class="custom-control-input" 
                        id="indicadorPermiteRenovar"
                        v-model="formPlan.indicadorPermiteRenovar"
                        :disabled="loading.guardar"
                      >
                      <label class="custom-control-label" for="indicadorPermiteRenovar">
                        Permite Renovación
                      </label>
                    </div>
                  </div>
                  
                  <div class="col-md-4">
                    <div class="form-group">
                      <label class="form-label">
                        Expiración para Renovar
                        <span v-if="obtenerInfoUnidadActual().tipo === 'indefinida'" class="badge badge-secondary ml-1">
                          <i class="nc-icon nc-info mr-1"></i>No aplica
                        </span>
                        <span v-else-if="obtenerInfoUnidadActual().tipo === 'dias'" class="badge badge-primary ml-1">
                          <i class="nc-icon nc-time-alarm mr-1"></i>Días
                        </span>
                        <span v-else-if="obtenerInfoUnidadActual().tipo === 'meses'" class="badge badge-success ml-1">
                          <i class="nc-icon nc-calendar-60 mr-1"></i>Meses
                        </span>
                      </label>
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="formPlan.expiracionRenovacion"
                        placeholder="Ej: 30"
                        min="0"
                        :disabled="loading.guardar || !formPlan.indicadorPermiteRenovar || obtenerInfoUnidadActual().tipo === 'indefinida'"
                      >
                      <small class="form-text" :class="{
                        'text-secondary': obtenerInfoUnidadActual().tipo === 'indefinida',
                        'text-primary': obtenerInfoUnidadActual().tipo === 'dias',
                        'text-success': obtenerInfoUnidadActual().tipo === 'meses',
                        'text-info': obtenerInfoUnidadActual().tipo === 'otra',
                        'text-muted': obtenerInfoUnidadActual().tipo === 'ninguna'
                      }">
                        <i class="nc-icon nc-info mr-1"></i>
                        {{ obtenerInfoUnidadActual().recomendacion }}
                      </small>
                    </div>
                  </div>
                  
                  <div class="col-md-4">
                    <div class="form-group">
                      <label class="form-label">Unidad de Expiración</label>
                      <select
                        class="form-control"
                        v-model="formPlan.unidadExpiracionRenovacion"
                        :disabled="loading.guardar || !formPlan.indicadorPermiteRenovar"
                      >
                        <option value="">-- Selecciona la unidad --</option>
                        <option 
                          v-for="unidad in catalogosCreacion.unidadesExpiracionRenovacion" 
                          :key="unidad._id"
                          :value="unidad._id"
                        >
                          {{ unidad.glosa }} - {{ unidad.descripcion }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <h6 class="mt-4 mb-3">Venta y Convenio</h6>
                <div class="row">
                  <div class="col-md-4">
                    <div class="custom-control custom-checkbox">
                      <input 
                        type="checkbox" 
                        class="custom-control-input" 
                        id="habilitarVenta"
                        v-model="formPlan.habilitarVenta"
                        :disabled="loading.guardar"
                      >
                      <label class="custom-control-label" for="habilitarVenta">
                        Habilitar Venta
                      </label>
                    </div>
                  </div>
                  
                  <div class="col-md-4">
                    <div class="custom-control custom-checkbox">
                      <input 
                        type="checkbox" 
                        class="custom-control-input" 
                        id="habilitarVentaMobile"
                        v-model="formPlan.habilitarVentaMobile"
                        :disabled="loading.guardar"
                      >
                      <label class="custom-control-label" for="habilitarVentaMobile">
                        Venta Mobile
                      </label>
                    </div>
                  </div>
                  
                  <div class="col-md-4">
                    <div class="custom-control custom-checkbox">
                      <input 
                        type="checkbox" 
                        class="custom-control-input" 
                        id="esSuscripcion"
                        v-model="formPlan.esSuscripcion"
                        :disabled="loading.guardar"
                      >
                      <label class="custom-control-label" for="esSuscripcion">
                        Es Suscripción
                      </label>
                    </div>
                  </div>
                </div>

                <div class="form-group mt-3">
                  <label class="form-label">Tipo de Convenio <span class="text-danger">*</span></label>
                  <select 
                    class="form-control"
                    id="convenio"
                    v-model="formPlan.convenio"
                    :disabled="loading.guardar"
                  >
                    <option value="">-- Selecciona el tipo de convenio --</option>
                    <option 
                      v-for="convenio in catalogosCreacion.convenios" 
                      :key="convenio._id"
                      :value="convenio._id"
                    >
                      {{ convenio.glosa }} - {{ convenio.descripcion }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- PESTAÑA TURNOS -->
              <div v-if="activeTabCreacion === 'turnos'" class="tab-pane active">
                <div class="alert alert-info mb-4">
                  <i class="nc-icon nc-bulb-63 mr-2"></i>
                  <strong>Selección de Turno:</strong> Selecciona un turno existente del sistema. Los turnos se gestionan desde el módulo de Turnos.
                </div>

                <!-- Loading de turnos -->
                <div v-if="loading.catalogos" class="text-center py-3">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Cargando turnos...</span>
                  </div>
                  <p class="mt-2 mb-0 text-muted">Cargando turnos disponibles...</p>
                </div>

                <!-- Lista de turnos disponibles -->
                <div v-else>
                  <h6 class="mb-3">
                    <i class="nc-icon nc-time-alarm mr-2"></i>
                    Turnos Disponibles ({{ catalogosCreacion.turnos.length }})
                  </h6>

                  <!-- No hay turnos -->
                  <div v-if="catalogosCreacion.turnos.length === 0" class="text-center py-4">
                    <i class="nc-icon nc-time-alarm" style="font-size: 3rem; color: #dee2e6;"></i>
                    <h6 class="mt-3">No hay turnos disponibles</h6>
                    <p class="text-muted">No se encontraron turnos activos en el sistema.</p>
                    <small class="text-muted">Crear turnos desde el módulo de Gestión de Turnos.</small>
                  </div>

                  <!-- Lista de turnos -->
                  <div v-else class="turnos-grid">
                    <div 
                      v-for="turno in catalogosCreacion.turnos" 
                      :key="turno._id"
                      class="turno-card"
                      :class="{ 'selected': formPlan.turnoSeleccionado === turno._id }"
                      @click="seleccionarTurno(turno)"
                    >
                      <div class="turno-header">
                        <div class="d-flex justify-content-between align-items-start">
                          <h6 class="turno-title mb-1">{{ turno.descripcionTurno }}</h6>
                          <div class="turno-id">
                            <span class="badge badge-outline-primary">ID: {{ turno.id }}</span>
                          </div>
                        </div>
                      </div>

                      <div class="turno-dias mb-2">
                        <small class="text-muted d-block mb-1">Días activos:</small>
                        <div class="dias-semana">
                          <span class="dia-badge" :class="{ 'activo': turno.lunesAutorizado }">L</span>
                          <span class="dia-badge" :class="{ 'activo': turno.martesAutorizado }">M</span>
                          <span class="dia-badge" :class="{ 'activo': turno.miercolesAutorizado }">X</span>
                          <span class="dia-badge" :class="{ 'activo': turno.juevesAutorizado }">J</span>
                          <span class="dia-badge" :class="{ 'activo': turno.viernesAutorizado }">V</span>
                          <span class="dia-badge" :class="{ 'activo': turno.sabadoAutorizado }">S</span>
                          <span class="dia-badge" :class="{ 'activo': turno.domingoAutorizado }">D</span>
                        </div>
                        <small class="text-success">{{ turno.totalDiasActivos }} día{{ turno.totalDiasActivos > 1 ? 's' : '' }} activo{{ turno.totalDiasActivos > 1 ? 's' : '' }}</small>
                      </div>

                      <div class="turno-horarios">
                        <small class="text-muted d-block mb-1">Horarios principales:</small>
                        <div class="horario-item">
                          <span class="horario-dia">L-V:</span>
                          <span class="horario-tiempo">{{ formatearHora(turno.lunesHoraInicio) }} - {{ formatearHora(turno.lunesHoraTermino) }}</span>
                        </div>
                        <div v-if="turno.sabadoAutorizado" class="horario-item">
                          <span class="horario-dia">Sáb:</span>
                          <span class="horario-tiempo">{{ formatearHora(turno.sabadoHoraInicio) }} - {{ formatearHora(turno.sabadoHoraTermino) }}</span>
                        </div>
                        <div v-if="turno.domingoAutorizado" class="horario-item">
                          <span class="horario-dia">Dom:</span>
                          <span class="horario-tiempo">{{ formatearHora(turno.domingoHoraInicio) }} - {{ formatearHora(turno.domingoHoraTermino) }}</span>
                        </div>
                      </div>

                      <!-- Indicador de selección -->
                      <div v-if="formPlan.turnoSeleccionado === turno._id" class="turno-selected">
                        <i class="nc-icon nc-check-2"></i>
                        <span>Seleccionado</span>
                      </div>
                    </div>
                  </div>

                  <!-- Turno seleccionado - Resumen -->
                  <div v-if="turnoSeleccionadoDetalle" class="turno-resumen mt-4">
                    <div class="card">
                      <div class="card-header">
                        <h6 class="mb-0">
                          <i class="nc-icon nc-check-2 text-success mr-2"></i>
                          Turno Seleccionado
                        </h6>
                      </div>
                      <div class="card-body">
                        <div class="row">
                          <div class="col-md-6">
                            <h6 class="text-primary">{{ turnoSeleccionadoDetalle.descripcionTurno }}</h6>
                            <p class="mb-2"><strong>ID:</strong> {{ turnoSeleccionadoDetalle.id }}</p>
                            <p class="mb-0"><strong>Días activos:</strong> {{ diasActivosTurno.join(', ') }}</p>
                          </div>
                          <div class="col-md-6">
                            <div class="table-responsive">
                              <table class="table table-sm">
                                <thead>
                                  <tr>
                                    <th>Día</th>
                                    <th>Horario</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-if="turnoSeleccionadoDetalle.lunesAutorizado">
                                    <td>Lunes</td>
                                    <td>{{ formatearHora(turnoSeleccionadoDetalle.lunesHoraInicio) }} - {{ formatearHora(turnoSeleccionadoDetalle.lunesHoraTermino) }}</td>
                                  </tr>
                                  <tr v-if="turnoSeleccionadoDetalle.martesAutorizado">
                                    <td>Martes</td>
                                    <td>{{ formatearHora(turnoSeleccionadoDetalle.martesHoraInicio) }} - {{ formatearHora(turnoSeleccionadoDetalle.martesHoraTermino) }}</td>
                                  </tr>
                                  <tr v-if="turnoSeleccionadoDetalle.miercolesAutorizado">
                                    <td>Miércoles</td>
                                    <td>{{ formatearHora(turnoSeleccionadoDetalle.miercolesHoraInicio) }} - {{ formatearHora(turnoSeleccionadoDetalle.miercolesHoraTermino) }}</td>
                                  </tr>
                                  <tr v-if="turnoSeleccionadoDetalle.juevesAutorizado">
                                    <td>Jueves</td>
                                    <td>{{ formatearHora(turnoSeleccionadoDetalle.juevesHoraInicio) }} - {{ formatearHora(turnoSeleccionadoDetalle.juevesHoraTermino) }}</td>
                                  </tr>
                                  <tr v-if="turnoSeleccionadoDetalle.viernesAutorizado">
                                    <td>Viernes</td>
                                    <td>{{ formatearHora(turnoSeleccionadoDetalle.viernesHoraInicio) }} - {{ formatearHora(turnoSeleccionadoDetalle.viernesHoraTermino) }}</td>
                                  </tr>
                                  <tr v-if="turnoSeleccionadoDetalle.sabadoAutorizado">
                                    <td>Sábado</td>
                                    <td>{{ formatearHora(turnoSeleccionadoDetalle.sabadoHoraInicio) }} - {{ formatearHora(turnoSeleccionadoDetalle.sabadoHoraTermino) }}</td>
                                  </tr>
                                  <tr v-if="turnoSeleccionadoDetalle.domingoAutorizado">
                                    <td>Domingo</td>
                                    <td>{{ formatearHora(turnoSeleccionadoDetalle.domingoHoraInicio) }} - {{ formatearHora(turnoSeleccionadoDetalle.domingoHoraTermino) }}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- PESTAÑA ADICIONAL -->
              <div v-if="activeTabCreacion === 'adicional'" class="tab-pane active">
                <h6 class="mb-3">Control de Acceso</h6>
                <div class="custom-control custom-checkbox mb-3">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="limitarPuntoAcceso"
                    v-model="formPlan.informacionAdicional.limitarPuntoAcceso"
                    :disabled="loading.guardar"
                  >
                  <label class="custom-control-label" for="limitarPuntoAcceso">
                    Limitar Puntos de Acceso
                  </label>
                </div>

                <div v-if="formPlan.informacionAdicional.limitarPuntoAcceso">
                  <div class="row mb-3">
                    <div class="col-md-8">
                      <input 
                        type="text" 
                        class="form-control"
                        v-model="nuevoPuntoAcceso.idAcceso"
                        placeholder="Ej: TORNIQUETE 1, ENTRADA PRINCIPAL..."
                      >
                    </div>
                    <div class="col-md-2">
                      <div class="custom-control custom-checkbox">
                        <input 
                          type="checkbox" 
                          class="custom-control-input" 
                          id="descontarSesion"
                          v-model="nuevoPuntoAcceso.descontarSesion"
                        >
                        <label class="custom-control-label" for="descontarSesion">
                          Descontar
                        </label>
                      </div>
                    </div>
                    <div class="col-md-2">
                      <button 
                        type="button" 
                        class="btn btn-primary btn-block"
                        @click="agregarPuntoAcceso"
                        :disabled="!nuevoPuntoAcceso.idAcceso.trim()"
                      >
                        <i class="nc-icon nc-simple-add"></i>
                      </button>
                    </div>
                  </div>

                  <div v-if="formPlan.informacionAdicional.puntoAccesoPermitidos.length > 0" class="table-responsive">
                    <table class="table table-sm">
                      <thead>
                        <tr>
                          <th>Punto de Acceso</th>
                          <th>Descontar Sesión</th>
                          <th width="50">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(punto, index) in formPlan.informacionAdicional.puntoAccesoPermitidos" :key="index">
                          <td>{{ punto.idAcceso }}</td>
                          <td>
                            <span class="badge" :class="punto.descontarSesion ? 'badge-warning' : 'badge-secondary'">
                              {{ punto.descontarSesion ? 'Sí' : 'No' }}
                            </span>
                          </td>
                          <td>
                            <button 
                              type="button" 
                              class="btn btn-sm btn-outline-danger"
                              @click="removerPuntoAcceso(index)"
                            >
                              <i class="nc-icon nc-simple-remove"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <h6 class="mt-4 mb-3">Beneficios Adicionales</h6>
                <div class="row mb-3">
                  <div class="col-md-10">
                    <input 
                      type="text" 
                      class="form-control"
                      v-model="nuevoBeneficio"
                      placeholder="Ej: Acceso a clases grupales, Uso de piscina..."
                    >
                  </div>
                  <div class="col-md-2">
                    <button 
                      type="button" 
                      class="btn btn-primary btn-block"
                      @click="agregarBeneficio"
                      :disabled="!nuevoBeneficio.trim()"
                    >
                      <i class="nc-icon nc-simple-add"></i>
                    </button>
                  </div>
                </div>

                <div v-if="formPlan.informacionAdicional.beneficiosAdicionales.length > 0">
                  <ul class="list-group">
                    <li 
                      v-for="(beneficio, index) in formPlan.informacionAdicional.beneficiosAdicionales" 
                      :key="index"
                      class="list-group-item d-flex justify-content-between align-items-center"
                    >
                      {{ beneficio }}
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-danger"
                        @click="removerBeneficio(index)"
                      >
                        <i class="nc-icon nc-simple-remove"></i>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- PESTAÑA SUCURSALES -->
              <div v-if="activeTabCreacion === 'sucursales'" class="tab-pane active">
                <div class="alert alert-info mb-4">
                  <i class="nc-icon nc-bulb-63 mr-2"></i>
                  <strong>Selección de Sucursales:</strong> Selecciona las sucursales donde este plan estará disponible para la venta. Si no seleccionas ninguna sucursal, el plan no estará disponible para la venta.
                </div>

                <!-- Loading de sucursales del usuario -->
                <div v-if="sucursalesUsuario.length === 0" class="text-center py-3">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Cargando sucursales...</span>
                  </div>
                  <p class="mt-2 mb-0 text-muted">Cargando sucursales disponibles...</p>
                </div>

                <!-- Lista de sucursales disponibles -->
                <div v-else>
                  <h6 class="mb-3">
                    <i class="nc-icon nc-pin-3 mr-2"></i>
                    Sucursales Disponibles ({{ sucursalesUsuario.length }})
                  </h6>

                  <!-- Controles de selección masiva -->
                  <div class="sucursales-controls mb-3">
                    <div class="row">
                      <div class="col-md-6">
                        <button 
                          type="button" 
                          class="btn btn-outline-primary btn-sm mr-2"
                          @click="seleccionarTodasSucursales"
                          :disabled="loading.guardar"
                        >
                          <i class="nc-icon nc-check-2 mr-1"></i>
                          Seleccionar Todas
                        </button>
                        <button 
                          type="button" 
                          class="btn btn-outline-secondary btn-sm"
                          @click="limpiarSucursalesSeleccionadas"
                          :disabled="loading.guardar"
                        >
                          <i class="nc-icon nc-simple-remove mr-1"></i>
                          Limpiar Selección
                        </button>
                      </div>
                      <div class="col-md-6 text-right">
                        <small class="text-muted">
                          {{ formPlan.sucursalesHabilitadas.length }} de {{ sucursalesUsuario.length }} seleccionadas
                        </small>
                      </div>
                    </div>
                  </div>

                  <!-- Grid de sucursales -->
                  <div class="sucursales-grid">
                    <div 
                      v-for="sucursal in sucursalesUsuario" 
                      :key="sucursal._id"
                      class="sucursal-card"
                      :class="{ 
                        'selected': isSucursalSelected(sucursal),
                        'fisica': sucursal.type === 'FISICA',
                        'virtual': sucursal.type === 'VIRTUAL'
                      }"
                      @click="toggleSucursal(sucursal)"
                    >
                      <div class="sucursal-header">
                        <div class="sucursal-type">
                          <i :class="getSucursalIcon(sucursal.type)"></i>
                          <span class="sucursal-type-text">{{ sucursal.type }}</span>
                        </div>
                        <div class="sucursal-checkbox">
                          <div class="custom-control custom-checkbox">
                            <input 
                              type="checkbox" 
                              class="custom-control-input" 
                              :id="'sucursal_' + sucursal._id"
                              :checked="isSucursalSelected(sucursal)"
                              @change="toggleSucursal(sucursal)"
                              :disabled="loading.guardar"
                            >
                            <label class="custom-control-label" :for="'sucursal_' + sucursal._id"></label>
                          </div>
                        </div>
                      </div>
                      
                      <div class="sucursal-content">
                        <h6 class="sucursal-name">{{ sucursal.nombreSucursal }}</h6>
                        <div class="sucursal-id">
                          <small class="text-muted">ID: {{ sucursal._id }}</small>
                        </div>
                      </div>

                      <!-- Indicador de selección -->
                      <div v-if="isSucursalSelected(sucursal)" class="sucursal-selected">
                        <i class="nc-icon nc-check-2"></i>
                        <span>Seleccionada</span>
                      </div>
                    </div>
                  </div>

                  <!-- Resumen de sucursales seleccionadas -->
                  <div v-if="formPlan.sucursalesHabilitadas.length > 0" class="sucursales-resumen mt-4">
                    <div class="card">
                      <div class="card-header">
                        <h6 class="mb-0">
                          <i class="nc-icon nc-check-2 text-success mr-2"></i>
                          Sucursales Seleccionadas ({{ formPlan.sucursalesHabilitadas.length }})
                        </h6>
                      </div>
                      <div class="card-body">
                        <div class="sucursales-seleccionadas-lista">
                          <div 
                            v-for="sucursal in formPlan.sucursalesHabilitadas" 
                            :key="sucursal._id"
                            class="sucursal-seleccionada-item"
                          >
                            <div class="sucursal-seleccionada-info">
                              <i :class="getSucursalIcon(sucursal.type || 'FISICA')"></i>
                              <span class="sucursal-seleccionada-nombre">{{ sucursal.nombreSucursal }}</span>
                              <small class="text-muted">({{ sucursal.type || 'FISICA' }})</small>
                            </div>
                            <button 
                              type="button" 
                              class="btn btn-sm btn-outline-danger"
                              @click="removerSucursal(sucursal._id)"
                              :disabled="loading.guardar"
                              title="Remover sucursal"
                            >
                              <i class="nc-icon nc-simple-remove"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Mensaje cuando no hay sucursales seleccionadas -->
                  <div v-else class="no-sucursales-seleccionadas mt-4">
                    <div class="alert alert-warning">
                      <i class="nc-icon nc-bell-55 mr-2"></i>
                      <strong>Atención:</strong> No has seleccionado ninguna sucursal. El plan no estará disponible para la venta si no seleccionas al menos una sucursal.
                    </div>
                  </div>
                </div>
              </div>

              </div>
            </div>
          </div>
          
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cerrarModalCreacion" :disabled="loading.guardar">
              Cancelar
            </button>
            <button 
              type="button" 
              class="btn btn-primary"
              @click="guardarPlan"
              :disabled="loading.guardar"
            >
              <span v-if="loading.guardar" class="spinner-border spinner-border-sm mr-2"></span>
              {{ loading.guardar ? (modoEdicion ? 'Actualizando...' : 'Creando...') : (modoEdicion ? 'Actualizar Plan' : 'Crear Plan') }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'PlanesManager',
  props: {
    catalogos: {
      type: Object,
      required: true,
      default: function() {
        return {
          familias: [],
          subfamilias: []
        }
      }
    },
    categoriaPreseleccionada: {
      type: Object,
      default: null
    }
  },
  data: function() {
    return {
      loading: {
        tabla: false,
        crear: false,
        editar: null,
        eliminar: null,
        verDetalle: null,
        exportar: false,
        refrescar: false,
        catalogos: false,
        guardar: false
      },
      planes: [],
      
      // Catálogos para creación
      catalogosCreacion: {
        unidadesDuracion: [],
        convenios: [],
        unidadesExpiracionRenovacion: [],
        sucursales: [],
        turnos: []
      },
      
      // Sucursales del usuario desde la sesión
      sucursalesUsuario: [],
      
      // Paginación
      pagination: {
        page: 1,
        limit: 10,
        totalPages: 1,
        totalRecords: 0,
        hasNext: false,
        hasPrev: false,
        nextPage: null,
        prevPage: null
      },
      
      // Ordenamiento
      ordenamiento: {
        campo: 'descripcion',
        direccion: 'asc'
      },
      
      // Filtros
      filtros: {
        descripcion: '',
        idFamilia: '',
        idSubFamilia: '',
        habilitarVenta: '',
        valorMin: null,
        valorMax: null,
        multiSede: '',
        activo: ''
      },
      
      // Modal y formulario de creación
      modoEdicion: false,
      planSeleccionado: null,
      activeTabCreacion: 'basico',
      formPlan: {
        // Básico
        familia: '',
        subfamilia: '',
        descripcion: '',
        valor: null,
        indicadorMatricula: false,
        valorMatricula: null,
        porcentajeDescuento: 0,
        
        // Facturación
        indicadorFactura: false,
        tipoIVA: 'AFECTO',
        
        // Duración
        duracion: 1,
        unidadDuracionPlan: '',
        indicadorMesCalendario: true,
        diasMesNoCalendario: 0,
        diasPermitidoCongelar: 0,
        
        // Sesiones
        indicadorUsoSesiones: false,
        sesionesLimite: 0,
        descuentoSesionesXDia: true,
        
        // Sedes
        multiSede: false,
        sucursalesHabilitadas: [],
        
        // Vigencia
        indicadorVigenciaTemporal: false,
        fechaInicioVigencia: '',
        fechaTerminoVigencia: '',
        
        // Renovación
        indicadorPermiteRenovar: true,
        expiracionRenovacion: 30,
        unidadExpiracionRenovacion: '',
        
        // Venta
        habilitarVenta: true,
        habilitarVentaMobile: true,
        esSuscripcion: false,
        convenio: '',
        
        // Turno
        turnoSeleccionado: null,
        
        // Información adicional
        informacionAdicional: {
          limitarPuntoAcceso: false,
          puntoAccesoPermitidos: [],
          beneficiosAdicionales: []
        }
      },
      
      // Campos temporales para agregar elementos
      nuevoPuntoAcceso: {
        idAcceso: '',
        descontarSesion: false
      },
      nuevoBeneficio: '',
      
      // Sistema de errores de validación
      erroresValidacion: {},
      
      // Debounce timer
      debounceTimer: null
    }
  },
  computed: {
    subfamiliasDisponibles: function() {
      if (!this.filtros.idFamilia || !this.catalogos.subfamilias) {
        return []
      }
      
      var self = this
      return this.catalogos.subfamilias.filter(function(subfamilia) {
        // Buscar por el ID numérico de la familia
        return subfamilia.familia && 
               (subfamilia.familia.id === parseInt(self.filtros.idFamilia) ||
                subfamilia.idFamilia === parseInt(self.filtros.idFamilia))
      })
    },
    
    subfamiliasFormulario: function() {
      if (!this.formPlan.familia || !this.catalogos.subfamilias) {
        return []
      }
      
      var self = this
      return this.catalogos.subfamilias.filter(function(subfamilia) {
        return subfamilia.familia && subfamilia.familia._id === self.formPlan.familia
      })
    },
    
    tienesFiltrosActivos: function() {
      return this.filtrosActivosDisplay.length > 0
    },
    
    filtrosActivosDisplay: function() {
      var activos = []
      var self = this
      
      if (this.filtros.descripcion) {
        activos.push({ key: 'descripcion', label: 'Descripción', value: this.filtros.descripcion })
      }
      if (this.filtros.idFamilia) {
        var familia = this.catalogos.familias.find(function(f) { return f.id === parseInt(self.filtros.idFamilia) })
        activos.push({ key: 'idFamilia', label: 'Familia', value: familia ? familia.nombre : this.filtros.idFamilia })
      }
      if (this.filtros.idSubFamilia) {
        var subfamilia = this.subfamiliasDisponibles.find(function(sf) { return sf.id === parseInt(self.filtros.idSubFamilia) })
        activos.push({ key: 'idSubFamilia', label: 'SubFamilia', value: subfamilia ? subfamilia.nombre : this.filtros.idSubFamilia })
      }
      if (this.filtros.habilitarVenta !== '') {
        activos.push({ key: 'habilitarVenta', label: 'Venta', value: this.filtros.habilitarVenta === 'true' ? 'Habilitada' : 'Deshabilitada' })
      }
      if (this.filtros.valorMin !== null) {
        activos.push({ key: 'valorMin', label: 'Precio mín', value: '$' + this.filtros.valorMin })
      }
      if (this.filtros.valorMax !== null) {
        activos.push({ key: 'valorMax', label: 'Precio máx', value: '$' + this.filtros.valorMax })
      }
      if (this.filtros.multiSede !== '') {
        activos.push({ key: 'multiSede', label: 'MultiSede', value: this.filtros.multiSede === 'true' ? 'Sí' : 'No' })
      }
      if (this.filtros.activo !== '') {
        activos.push({ key: 'activo', label: 'Estado', value: this.filtros.activo === 'true' ? 'Activos' : 'Inactivos' })
      }
      
      return activos
    },
    
    paginasVisibles: function() {
      var total = this.pagination.totalPages
      var actual = this.pagination.page
      var visibles = []
      
      var inicio = Math.max(1, actual - 2)
      var fin = Math.min(total, actual + 2)
      
      for (var i = inicio; i <= fin; i++) {
        visibles.push(i)
      }
      
      return visibles
    },
    
    turnoSeleccionadoDetalle: function() {
      if (!this.formPlan.turnoSeleccionado) return null
      
      var self = this
      return this.catalogosCreacion.turnos.find(function(turno) {
        return turno._id === self.formPlan.turnoSeleccionado
      })
    },
    
    diasActivosTurno: function() {
      if (!this.turnoSeleccionadoDetalle) return []
      
      var dias = []
      var turno = this.turnoSeleccionadoDetalle
      
      if (turno.lunesAutorizado) dias.push('Lunes')
      if (turno.martesAutorizado) dias.push('Martes')
      if (turno.miercolesAutorizado) dias.push('Miércoles')
      if (turno.juevesAutorizado) dias.push('Jueves')
      if (turno.viernesAutorizado) dias.push('Viernes')
      if (turno.sabadoAutorizado) dias.push('Sábado')
      if (turno.domingoAutorizado) dias.push('Domingo')
      
      return dias
    }
  },
  watch: {
    categoriaPreseleccionada: {
      immediate: true,
      handler(nuevaCategoria, categoriaAnterior) {
        if (nuevaCategoria && nuevaCategoria.subfamiliaId) {
          this.$logger.log('🎯 Categoría preseleccionada recibida:', nuevaCategoria)
          
          // Limpiar filtros primero
          this.filtros.idFamilia = ''
          this.filtros.idSubFamilia = ''
          
          // Aplicar filtro de subfamilia (siempre presente)
          this.filtros.idSubFamilia = nuevaCategoria.subfamiliaId.toString()
          
          // Aplicar filtro de familia solo si está disponible
          if (nuevaCategoria.familiaId) {
            this.filtros.idFamilia = nuevaCategoria.familiaId.toString()
            this.$logger.log('✅ Filtros aplicados (Familia + SubFamilia):', {
              familia: nuevaCategoria.familiaNombre,
              subfamilia: nuevaCategoria.subfamiliaNombre
            })
          } else {
            this.$logger.log('✅ Filtros aplicados (Solo SubFamilia):', {
              subfamilia: nuevaCategoria.subfamiliaNombre
            })
          }
          
          // Aplicar filtros después de que el componente esté montado
          this.$nextTick(() => {
            this.aplicarFiltros()
          })
        } else if (nuevaCategoria === null && categoriaAnterior) {
          // Cuando se limpia la categoría preseleccionada (Ver todos los planes)
          this.$logger.log('🧹 Limpiando filtros de categoría preseleccionada')
          
          // Limpiar solo los filtros de familia y subfamilia, mantener otros filtros
          this.filtros.idFamilia = ''
          this.filtros.idSubFamilia = ''
          
          // Resetear paginación y recargar
          this.$nextTick(() => {
            this.aplicarFiltros()
          })
        }
      }
    },

    // Watcher para el checkbox "Permite Renovación"
    // Al desmarcar, los campos siguen visibles pero quedan deshabilitados con
    // valores fijos: expiracionRenovacion = 0 y unidadExpiracionRenovacion = INDEFINIDA
    'formPlan.indicadorPermiteRenovar': {
      handler(permiteRenovacion) {
        if (!this.formPlan) return

        if (!permiteRenovacion) {
          this.formPlan.expiracionRenovacion = 0
          const unidadIndefinida = this.obtenerUnidadIndefinida()
          this.formPlan.unidadExpiracionRenovacion = unidadIndefinida ? unidadIndefinida._id : ''
        }
      }
    },

    // Watcher para Unidad de Expiración - aplicar validaciones según tipo de unidad
    'formPlan.unidadExpiracionRenovacion': {
      handler(unidadId) {
        if (!this.formPlan) return
        if (!this.formPlan.indicadorPermiteRenovar) return // Solo si permite renovación
        
        const infoUnidad = this.obtenerInfoUnidadActual()
        
        if (infoUnidad.tipo === 'indefinida') {
          // INDEFINIDA (I): Valor debe ser 0 y campo bloqueado
          this.formPlan.expiracionRenovacion = 0
        } else if (infoUnidad.tipo === 'dias' || infoUnidad.tipo === 'meses') {
          // DIAS (D) o MESES (M): Si el valor actual es 0, establecer un valor por defecto
          if (this.formPlan.expiracionRenovacion === 0) {
            // Valores sugeridos por defecto
            this.formPlan.expiracionRenovacion = infoUnidad.tipo === 'dias' ? 30 : 1
          }
        }
      }
    }
  },
  created() {
    // Sobreescribir $swal a nivel de instancia con un mixin que asegura
    // que TODOS los modales/toasts/loadings de SweetAlert2 disparados desde
    // este componente queden por encima del modal de Crear/Editar Plan
    // (clase global definida al final del <style> con z-index alto).
    if (this.$swal && typeof this.$swal.mixin === 'function') {
      this.$swal = this.$swal.mixin({
        customClass: {
          container: 'swal-sobre-modal-plan'
        }
      })
    }
  },
  async mounted() {
    await Promise.all([
      this.cargarPlanes(),
      this.cargarCatalogosCreacion(),
      this.cargarSucursalesUsuario()
    ])
  },
  methods: {
    formatoCurrency: function(valor) {
      return '$' + valor
    },

    // Método auxiliar para obtener unidad por ID
    obtenerUnidadPorId: function(id) {
      // Validación defensiva: verificar que el catálogo existe y el ID es válido
      if (!this.catalogosCreacion || !this.catalogosCreacion.unidadesExpiracionRenovacion || !Array.isArray(this.catalogosCreacion.unidadesExpiracionRenovacion) || !id) {
        return null
      }
      
      return this.catalogosCreacion.unidadesExpiracionRenovacion.find(function(unidad) {
        return unidad && unidad._id === id
      })
    },

    // Método para obtener información de la unidad actual
    obtenerInfoUnidadActual: function() {
      if (!this.formPlan || !this.formPlan.unidadExpiracionRenovacion) {
        return { tipo: 'ninguna', glosa: '', recomendacion: 'Seleccione primero una unidad de expiración' }
      }
      
      const unidadSeleccionada = this.obtenerUnidadPorId(this.formPlan.unidadExpiracionRenovacion)
      if (!unidadSeleccionada || !unidadSeleccionada.glosa) {
        return { tipo: 'ninguna', glosa: '', recomendacion: 'Unidad no encontrada' }
      }
      
      const glosa = unidadSeleccionada.glosa.toUpperCase()
      
      if (glosa.includes('INDEFINIDA')) {
        return { 
          tipo: 'indefinida', 
          glosa: unidadSeleccionada.glosa,
          recomendacion: 'Los valores numéricos no se consideran. La renovación no expira.'
        }
      } else if (glosa.includes('DIAS') || glosa.includes('DÍA')) {
        return { 
          tipo: 'dias', 
          glosa: unidadSeleccionada.glosa,
          recomendacion: 'Ingrese la cantidad de días antes del vencimiento para volver a cobrar la matrícula del Plan.'
        }
      } else if (glosa.includes('MESES') || glosa.includes('MES')) {
        return { 
          tipo: 'meses', 
          glosa: unidadSeleccionada.glosa,
          recomendacion: 'Ingrese la cantidad de meses antes del vencimiento para volver a cobrar la matrícula del Plan.'
        }
      } else {
        return { 
          tipo: 'otra', 
          glosa: unidadSeleccionada.glosa,
          recomendacion: 'Ingrese el valor numérico correspondiente a la unidad seleccionada.'
        }
      }
    },



    cargarSucursalesUsuario: function() {
      try {
        this.$logger.log('🏢 Cargando sucursales del usuario desde la sesión...')
        
        // Obtener sucursales desde sessionStorage (mismo método que UserProfile)
        const sucursalesSession = sessionStorage.getItem('xxxwypt5ip')
        if (sucursalesSession) {
          this.sucursalesUsuario = JSON.parse(sucursalesSession)
          this.$logger.log('✅ Sucursales del usuario cargadas:', this.sucursalesUsuario.length, 'sucursales')
          this.$logger.log('📋 Lista de sucursales:', this.sucursalesUsuario.map(s => ({
            id: s._id,
            nombre: s.nombreSucursal,
            tipo: s.type
          })))
        } else {
          console.warn('⚠️ No se encontraron sucursales en la sesión del usuario')
          this.sucursalesUsuario = []
        }
      } catch (error) {
        this.$logger.error('❌ Error al cargar sucursales del usuario:', error)
        this.sucursalesUsuario = []
      }
    },

    cargarCatalogosCreacion: async function() {
      try {
        this.loading.catalogos = true
        this.$logger.log('📊 Cargando catálogos para creación de planes')
        
        // Cargar catálogos principales de planes
        var responseCatalogos = await this.$CentralGymOPS_API({
          method: 'POST',
          url: '/',
          headers: {
            'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
          },
          data: {
            Header: {
              Operacion: '/v1/planes/catalogo'
            },
            Auth: {
              Token: sessionStorage.getItem('jCxbraoQeD')
            },
            Parameters: {},
            Body: {}
          }
        })

        this.$logger.log('📊 Catálogos obtenidos:', responseCatalogos.data)

        if (responseCatalogos.data && responseCatalogos.data.dataOut) {
          this.catalogosCreacion.unidadesDuracion = responseCatalogos.data.dataOut.unidadesDuracion || []
          this.catalogosCreacion.convenios = responseCatalogos.data.dataOut.convenios || []
          this.catalogosCreacion.unidadesExpiracionRenovacion = responseCatalogos.data.dataOut.unidadesExpiracionRenovacion || []
        }
        
        // Cargar catálogo de sucursales para multiSede
        try {
          var responseSucursales = await this.$CentralGymOPS_API({
            method: 'POST',
            url: '/',
            headers: {
              'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
            },
            data: {
              Header: {
                Operacion: '/v1/sucursales/consultar'
              },
              Auth: {
                Token: sessionStorage.getItem('jCxbraoQeD')
              },
              Parameters: {
                activo: true
              },
              Body: {}
            }
          })

          if (responseSucursales.data && responseSucursales.data.dataOut && responseSucursales.data.dataOut.sucursales) {
            this.catalogosCreacion.sucursales = responseSucursales.data.dataOut.sucursales
          }
        } catch (errorSucursales) {
          console.warn('⚠️ No se pudieron cargar las sucursales:', errorSucursales)
          this.catalogosCreacion.sucursales = []
        }
        
        // Cargar catálogo de turnos
        try {
          var responseTurnos = await this.$CentralGymOPS_API({
            method: 'POST',
            url: '/',
            headers: {
              'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
            },
            data: {
              Header: {
                Operacion: '/v1/turno/consultar-por-empresa'
              },
              Auth: {
                Token: sessionStorage.getItem('jCxbraoQeD')
              },
              Parameters: {},
              Body: {}
            }
          })

          if (responseTurnos.data && responseTurnos.data.data && responseTurnos.data.data.turnos) {
            this.catalogosCreacion.turnos = responseTurnos.data.data.turnos.filter(function(turno) {
              return turno.activo === true
            })
          }
        } catch (errorTurnos) {
          console.warn('⚠️ No se pudieron cargar los turnos:', errorTurnos)
          this.catalogosCreacion.turnos = []
        }
        
        this.$logger.log('✅ Catálogos cargados:', {
          unidadesDuracion: this.catalogosCreacion.unidadesDuracion.length,
          convenios: this.catalogosCreacion.convenios.length,
          unidadesExpiracionRenovacion: this.catalogosCreacion.unidadesExpiracionRenovacion.length,
          sucursales: this.catalogosCreacion.sucursales.length,
          turnos: this.catalogosCreacion.turnos.length
        })
        
      } catch (error) {
        this.$logger.error('❌ Error al cargar catálogos:', error)
        this.$swal.fire({
          title: 'Error al Cargar Catálogos',
          text: 'No se pudieron cargar los catálogos para la creación de planes',
          icon: 'error',
          toast: true,
          position: 'top-end',
          timer: 4000,
          showConfirmButton: false
        })
      } finally {
        this.loading.catalogos = false
      }
    },

    cargarPlanes: async function() {
      try {
        this.loading.tabla = true
        this.$logger.log('📊 Cargando planes con filtros:', this.getFiltrosActivos())
        
        var response = await this.$CentralGymOPS_API({
          method: 'POST',
          url: '/',
          headers: {
            'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
          },
          data: {
            Header: {
              Operacion: '/v1/planes/consultar-paginados'
            },
            Auth: {
              Token: sessionStorage.getItem('jCxbraoQeD')
            },
            Parameters: Object.assign({
              page: this.pagination.page,
              limit: this.pagination.limit,
              sortBy: this.ordenamiento.campo,
              sortOrder: this.ordenamiento.direccion
            }, this.getFiltrosActivos()),
            Body: {}
          }
        })

        this.$logger.log('📊 Respuesta planes:', response.data)

        if (response.data && response.data.dataOut && response.data.dataOut.planes) {
          this.planes = response.data.dataOut.planes
          
          // Actualizar paginación con nueva estructura
          const paginationData = response.data.dataOut.pagination || {}
          const currentPage = paginationData.page || this.pagination.page
          const totalPages = paginationData.totalPages || this.pagination.totalPages
          const hasPrev = paginationData.hasPrev !== undefined ? paginationData.hasPrev : (currentPage > 1)
          const hasNext = paginationData.hasNext !== undefined ? paginationData.hasNext : (currentPage < totalPages)
          
          this.pagination = {
            ...this.pagination,
            ...paginationData,
            totalRecords: response.data.dataOut.total || this.pagination.totalRecords,
            // Calcular prevPage y nextPage basándose en la respuesta del servidor
            prevPage: hasPrev ? (currentPage - 1) : null,
            nextPage: hasNext ? (currentPage + 1) : null,
            hasPrev: hasPrev,
            hasNext: hasNext
          }
          
          // Información adicional de la respuesta
          const filtrosInfo = response.data.dataOut.filtros || {}
          const analisisInfo = response.data.dataOut.analisis || {}
          
          this.$logger.log('📊 Información adicional:', {
            filtrosAplicados: filtrosInfo.aplicados,
            encontrados: filtrosInfo.encontrados,
            totalPlanes: analisisInfo.totalPlanes,
            rangoPrecios: analisisInfo.rangoPrecios,
            distribucionFamilias: analisisInfo.distribucionFamilias
          })
          
          // Emitir estadísticas actualizadas usando los nuevos campos
          this.$emit('actualizarEstadisticas', {
            totalPlanes: response.data.dataOut.total || this.pagination.totalRecords,
            planesActivos: this.planes.filter(function(p) { return p.activo }).length,
            planesInactivos: this.planes.filter(function(p) { return !p.activo }).length,
            // Estadísticas adicionales del análisis
            rangoPrecios: analisisInfo.rangoPrecios,
            distribucionFamilias: analisisInfo.distribucionFamilias
          })
          
          this.$logger.log('✅ Planes cargados:', this.planes.length, 'de', response.data.dataOut.total || 'total desconocido')
          this.$logger.log('📄 Paginación actualizada:', {
            page: this.pagination.page,
            totalPages: this.pagination.totalPages,
            hasPrev: this.pagination.hasPrev,
            hasNext: this.pagination.hasNext,
            prevPage: this.pagination.prevPage,
            nextPage: this.pagination.nextPage
          })
        } else {
          console.warn('⚠️ No se encontraron planes en la respuesta')
          this.planes = []
        }
        
      } catch (error) {
        this.$logger.error('❌ Error al cargar planes:', error)
        
        // Manejo específico de errores
        var errorMessage = 'No se pudieron cargar los planes'
        
        if (error.response && error.response.status === 401) {
          errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
          var self = this
          setTimeout(function() {
            sessionStorage.clear()
            self.$router.push('/login')
          }, 2000)
        } else if (error.response && error.response.status === 400) {
          errorMessage = error.response.data && error.response.data.result && error.response.data.result.message || 'Error en los parámetros de la consulta'
        } else if (error.response && error.response.status === 500) {
          errorMessage = 'Error interno del servidor. Inténtalo nuevamente.'
        }
        
        this.$swal.fire({
          title: 'Error al Cargar Planes',
          text: errorMessage,
          icon: 'error',
          toast: true,
          position: 'top-end',
          timer: 4000,
          showConfirmButton: false
        })
        
        this.planes = []
      } finally {
        this.loading.tabla = false
      }
    },

    getFiltrosActivos: function() {
      var filtros = {}
      
      if (this.filtros.descripcion.trim()) {
        filtros.descripcion = this.filtros.descripcion.trim()
      }
      
      if (this.filtros.idFamilia) {
        filtros.idFamilia = parseInt(this.filtros.idFamilia)
      }
      
      if (this.filtros.idSubFamilia) {
        filtros.idSubFamilia = parseInt(this.filtros.idSubFamilia)
      }
      
      if (this.filtros.habilitarVenta !== '') {
        filtros.habilitarVenta = this.filtros.habilitarVenta === 'true'
      }
      
      if (this.filtros.valorMin !== null && this.filtros.valorMin > 0) {
        filtros.valorMin = this.filtros.valorMin
      }
      
      if (this.filtros.valorMax !== null && this.filtros.valorMax > 0) {
        filtros.valorMax = this.filtros.valorMax
      }
      
      if (this.filtros.multiSede !== '') {
        filtros.multiSede = this.filtros.multiSede === 'true'
      }
      
      if (this.filtros.activo !== '') {
        filtros.activo = this.filtros.activo === 'true'
      }
      
      return filtros
    },

    // Filtros y ordenamiento
    debounceFilter: function() {
      var self = this
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(function() {
        self.aplicarFiltros()
      }, 500)
    },

    aplicarFiltros: async function() {
      this.pagination.page = 1
      await this.cargarPlanes()
    },

    onFamiliaChange: function() {
      // Limpiar subfamilia cuando cambia la familia
      this.filtros.idSubFamilia = ''
      this.aplicarFiltros()
    },

    limpiarFiltros: function() {
      this.filtros = {
        descripcion: '',
        idFamilia: '',
        idSubFamilia: '',
        habilitarVenta: '',
        valorMin: null,
        valorMax: null,
        multiSede: '',
        activo: ''
      }
      this.aplicarFiltros()
    },

    removerFiltro: function(filtroKey) {
      if (filtroKey === 'valorMin' || filtroKey === 'valorMax') {
        this.filtros[filtroKey] = null
      } else {
        this.filtros[filtroKey] = ''
      }
      
      // Si se remueve la familia, también limpiar subfamilia
      if (filtroKey === 'idFamilia') {
        this.filtros.idSubFamilia = ''
      }
      
      this.aplicarFiltros()
    },

    sortBy: async function(campo) {
      if (this.ordenamiento.campo === campo) {
        this.ordenamiento.direccion = this.ordenamiento.direccion === 'asc' ? 'desc' : 'asc'
      } else {
        this.ordenamiento.campo = campo
        this.ordenamiento.direccion = 'asc'
      }
      
      await this.cargarPlanes()
    },

    cambiarPagina: async function(pagina) {
      if (pagina && pagina !== this.pagination.page) {
        this.pagination.page = pagina
        await this.cargarPlanes()
      }
    },

    cambiarLimite: async function() {
      this.pagination.page = 1
      await this.cargarPlanes()
    },

    refreshPlanes: async function() {
      try {
        this.loading.refrescar = true
        await this.cargarPlanes()
        this.$emit('refresh')
        
        this.$swal.fire({
          title: 'Actualizado',
          text: 'Los planes han sido actualizados',
          icon: 'success',
          showConfirmButton: true,
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-success',
          allowOutsideClick: false,
          allowEscapeKey: false
        })
      } catch (error) {
        this.$logger.error('Error al refrescar planes:', error)
        this.$swal.fire({
          title: 'Error',
          text: 'No se pudo refrescar la lista de planes',
          icon: 'error',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false
        })
      } finally {
        this.loading.refrescar = false
      }
    },

    // CRUD Operations
    abrirModalCrear: function() {
      this.$logger.log('🎯 Abriendo modal para crear nuevo plan')
      
      this.modoEdicion = false
      this.planSeleccionado = null
      this.activeTabCreacion = 'basico'
      this.limpiarFormulario()
      
      // Determinar qué valores usar para preseleccionar Familia y SubFamilia
      // Prioridad: Filtros activos > Categoría preseleccionada
      let familiaAPreseleccionar = null
      let subfamiliaAPreseleccionar = null
      let fuentePreseleccion = 'ninguna'
      
      // 1. Verificar si hay filtros activos de Familia y/o SubFamilia
      if (this.filtros.idFamilia || this.filtros.idSubFamilia) {
        this.$logger.log('📋 Detectados filtros activos en la interfaz')
        
        // Buscar los IDs de MongoDB basados en los IDs numéricos de los filtros
        if (this.filtros.idFamilia) {
          const familiaFiltrada = this.catalogos.familias.find(function(f) {
            return f.id === parseInt(this.filtros.idFamilia)
          }.bind(this))
          
          if (familiaFiltrada) {
            familiaAPreseleccionar = familiaFiltrada._id
            this.$logger.log('✅ Familia desde filtro:', familiaFiltrada.nombre)
          }
        }
        
        if (this.filtros.idSubFamilia) {
          const subfamiliaFiltrada = this.catalogos.subfamilias.find(function(sf) {
            return sf.id === parseInt(this.filtros.idSubFamilia)
          }.bind(this))
          
          if (subfamiliaFiltrada) {
            subfamiliaAPreseleccionar = subfamiliaFiltrada._id
            this.$logger.log('✅ SubFamilia desde filtro:', subfamiliaFiltrada.nombre)
          }
        }
        
        fuentePreseleccion = 'filtros'
      }
      // 2. Si no hay filtros activos, usar categoría preseleccionada (navegación desde SubFamilias)
      else if (this.categoriaPreseleccionada) {
        this.$logger.log('📋 Usando categoría preseleccionada de navegación')
        
        if (this.categoriaPreseleccionada.familia_id) {
          familiaAPreseleccionar = this.categoriaPreseleccionada.familia_id
        }
        
        if (this.categoriaPreseleccionada.subfamilia_id) {
          subfamiliaAPreseleccionar = this.categoriaPreseleccionada.subfamilia_id
        }
        
        fuentePreseleccion = 'navegacion'
      }
      
      // 3. Aplicar los valores preseleccionados al formulario
      if (familiaAPreseleccionar) {
        this.formPlan.familia = familiaAPreseleccionar
        this.$logger.log('✅ Familia preseleccionada aplicada:', familiaAPreseleccionar)
      }
      
      if (subfamiliaAPreseleccionar) {
        // Esperar a que se actualicen las subfamilias disponibles
        this.$nextTick(function() {
          this.formPlan.subfamilia = subfamiliaAPreseleccionar
          this.$logger.log('✅ SubFamilia preseleccionada aplicada:', subfamiliaAPreseleccionar)
        }.bind(this))
      }
      
      this.$logger.log('📊 Resumen de preselección:', {
        fuente: fuentePreseleccion,
        familiaId: familiaAPreseleccionar || 'Sin preseleccionar',
        subfamiliaId: subfamiliaAPreseleccionar || 'Sin preseleccionar'
      })
      
      this.$nextTick(function() {
        this.mostrarModalCreacion()
      }.bind(this))
    },

    editarPlan: function(plan) {
      this.$logger.log('✏️ Editando plan:', plan)
      
      this.loading.editar = plan.id
      this.modoEdicion = true
      this.planSeleccionado = plan
      this.activeTabCreacion = 'basico'
      
      this.cargarDatosPlanEnFormulario(plan)
      
      this.$nextTick(function() {
        this.mostrarModalCreacion()
        this.loading.editar = null
      }.bind(this))
    },

    async confirmarEliminar(plan) {
      // Activar loading inmediatamente cuando se hace clic en eliminar
      this.loading.eliminar = plan.id
      
      // Crear mensaje dinámico basado en el estado del plan
      const esEtapa1 = plan.activo
      const titulo = esEtapa1 ? '¿Eliminar Plan? (Etapa 1)' : '¿Eliminar Plan Permanentemente? (Etapa 2)'
      
      let mensajeDetallado = `
        <div class="text-center">
          <p style="font-size: 1.1rem; margin-bottom: 15px;">
            ¿Estás seguro de que deseas ${esEtapa1 ? 'desactivar' : 'eliminar permanentemente'} el plan <strong>"${plan.descripcion}"</strong>?
          </p>
          
          ${esEtapa1 ? `
            <div class="alert alert-info text-left" style="margin: 15px 0;">
              <i class="nc-icon nc-single-02"></i>
              <strong>🔄 Eliminación por Etapas:</strong>
              <ul style="margin: 8px 0 0 20px;">
                <li><strong>Etapa 1 (actual):</strong> Eliminación lógica - se desactiva el plan</li>
                <li><strong>Etapa 2 (posterior):</strong> Eliminación física - se remueve permanentemente</li>
              </ul>
            </div>
          ` : `
            <div class="alert alert-danger text-left" style="margin: 15px 0;">
              <i class="nc-icon nc-simple-remove"></i>
              <strong>⚠️ Eliminación Permanente:</strong>
              <p style="margin: 8px 0;">Este plan ya está inactivo. Esta acción lo eliminará <strong>permanentemente</strong> del sistema.</p>
            </div>
          `}
          
          <div class="alert ${esEtapa1 ? 'alert-warning' : 'alert-danger'} text-left" style="margin: 15px 0;">
            <i class="nc-icon nc-bell-55"></i>
            <strong>ATENCIÓN - Impacto de la eliminación:</strong>
            <ul style="margin: 10px 0 0 20px;">
              ${esEtapa1 ? '<li>El plan será marcado como <strong>inactivo</strong></li>' : '<li>El plan será <strong>eliminado permanentemente</strong></li>'}
              <li>No estará disponible para nuevas ventas</li>
              ${esEtapa1 ? '<li>Esta acción puede ser reversible desde la administración</li>' : '<li>Esta acción es <strong>irreversible</strong></li>'}
            </ul>
          </div>
          
          <div style="margin: 15px 0; padding: 15px; background: rgba(108, 117, 125, 0.1); border-radius: 8px;">
            <p style="margin: 0; font-size: 0.9rem; text-align: left;">
              <strong>📋 Información del plan:</strong><br>
              • <strong>ID:</strong> ${plan.id}<br>
              • <strong>Familia:</strong> ${plan.nombreFamilia}<br>
              • <strong>SubFamilia:</strong> ${plan.nombreSubFamilia}<br>
              • <strong>Precio:</strong> $${this.formatearPrecioExacto(plan.valor)}<br>
              • <strong>Estado actual:</strong> ${plan.activo ? 'Activo' : 'Inactivo'}
            </p>
          </div>
        </div>
      `
      
      this.$swal.fire({
        title: titulo,
        html: mensajeDetallado,
        icon: esEtapa1 ? 'warning' : 'error',
        showCancelButton: true,
        confirmButtonText: esEtapa1 
          ? 'Sí, desactivar plan (Etapa 1)'
          : 'Sí, eliminar permanentemente (Etapa 2)',
        cancelButtonText: 'Cancelar',
        confirmButtonClass: 'btn btn-danger',
        cancelButtonClass: 'btn btn-secondary',
        reverseButtons: true,
        focusCancel: true,
        customClass: {
          container: 'swal-eliminar-plan-etapas'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.eliminarPlan(plan)
        } else {
          // Limpiar loading si el usuario cancela
          this.loading.eliminar = null
        }
      })
    },

    async eliminarPlan(plan) {
      try {
        // Mostrar modal de loading inmediatamente después de la confirmación
        this.$swal.fire({
          title: 'Eliminando Plan...',
          html: `
            <div class="text-center">
              <div class="spinner-border text-danger" style="width: 3rem; height: 3rem;" role="status">
                <span class="sr-only">Eliminando...</span>
              </div>
              <p class="mt-3" style="font-size: 1.1rem;">
                Procesando eliminación de <strong>"${plan.descripcion}"</strong>
              </p>
              <p class="text-muted" style="font-size: 0.9rem;">
                Por favor espera mientras se completa la operación...
              </p>
            </div>
          `,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          customClass: {
            container: 'swal-loading-eliminar-plan'
          }
        })

        this.$logger.log('🗑️ Eliminando plan:', {
          id: plan.id,
          descripcion: plan.descripcion,
          activo: plan.activo
        })

        const response = await this.$CentralGymOPS_API({
          method: 'POST',
          url: '/',
          headers: {
            'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
          },
          data: {
            Header: { Operacion: '/v1/planes/eliminar' },
            Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
            Parameters: { id: plan.id },
            Body: {}
          }
        })

        this.$logger.log('🗑️ Respuesta del servidor:', response.data)

        // Verificar respuesta exitosa según especificación
        if (response.data?.result?.code === 0 && response.data?.result?.httpCode === 200) {
          const planEliminado = response.data?.dataOut
          const mensaje = response.data?.result?.message || 'Plan eliminado exitosamente'
          
          // Determinar si fue etapa 1 o etapa 2 basado en el estado anterior del plan
          const fueEtapa1 = plan.activo
          
          // Crear mensaje detallado para el usuario
          let mensajeDetallado = `
            <div class="text-center">
              <i class="nc-icon nc-check-2" style="font-size: 3rem; color: #6bd098; margin-bottom: 15px;"></i>
              <h5 style="color: #28a745; margin-bottom: 15px;">¡${fueEtapa1 ? 'Etapa 1' : 'Etapa 2'} Completada!</h5>
              
              <div class="alert alert-success text-left" style="margin: 15px 0;">
                <strong>✅ Plan ${fueEtapa1 ? 'desactivado' : 'eliminado permanentemente'}:</strong>
                <ul style="margin: 5px 0 0 20px;">
                  <li>"${planEliminado ? planEliminado.descripcion : plan.descripcion}" (ID: ${planEliminado ? planEliminado.id : plan.id})</li>
                  <li><strong>Estado:</strong> ${fueEtapa1 ? 'Inactivo' : 'Eliminado del sistema'}</li>
                </ul>
              </div>
              
              <div style="margin-top: 15px; padding: 10px; background: rgba(40, 167, 69, 0.1); border-radius: 4px;">
                <small class="text-muted">
                  <strong>💡 ${fueEtapa1 ? 'Etapa 1 Completada:' : 'Etapa 2 Completada:'}</strong> ${fueEtapa1 ? 'Eliminación lógica exitosa. El plan se conserva en la base de datos pero está marcado como inactivo.' : 'Eliminación física exitosa. El plan ha sido removido permanentemente del sistema.'}
                </small>
              </div>
              
              ${fueEtapa1 ? `
                <div style="margin-top: 10px; padding: 10px; background: rgba(255, 193, 7, 0.1); border-radius: 4px; border-left: 4px solid #ffc107;">
                  <small class="text-muted">
                    <strong>🔄 Próximo Paso:</strong> Puedes realizar una segunda eliminación para remover permanentemente el plan del sistema.
                  </small>
                </div>
              ` : ''}
            </div>
          `
          
          await this.$swal.fire({
            title: '',
            html: mensajeDetallado,
            icon: 'success',
            showConfirmButton: true,
            confirmButtonText: 'Entendido',
            confirmButtonClass: 'btn btn-success',
            toast: false,
            position: 'center',
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
              container: 'swal-eliminacion-plan-exitosa'
            }
          })

          // Recargar lista de planes y actualizar estadísticas DESPUÉS de que el usuario cierre el modal
          await this.cargarPlanes()
          this.$emit('refresh')
          
        } else {
          // Otros errores del servidor
          throw new Error(response.data?.result?.message || 'Error desconocido del servidor')
        }

      } catch (error) {
        this.$logger.error('❌ Error al eliminar plan:', error)
        this.$logger.log('📊 Detalles del error:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        })
        
        let errorMessage = 'No se pudo eliminar el plan'
        let errorTitle = 'Error al Eliminar'
        
        if (error.response?.status === 500) {
          // Error del servidor - plan no encontrado
          errorMessage = error.response.data?.result?.message || 
                        error.response.data?.result?.errors?.[0] ||
                        'Plan no encontrado en el sistema'
          errorTitle = 'Plan No Encontrado'
        } else if (error.response?.status === 401) {
          errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
          errorTitle = 'Sesión Expirada'
          // Opcional: redirigir al login
          setTimeout(() => {
            sessionStorage.clear()
            this.$router.push('/login')
          }, 2000)
        } else if (error.response?.status === 403) {
          errorMessage = 'No tienes permisos para eliminar este plan.'
          errorTitle = 'Acceso Denegado'
        } else if (error.message) {
          errorMessage = error.message
        }

        this.$swal.fire({
          title: errorTitle,
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-danger'
        })
      } finally {
        this.loading.eliminar = null
      }
    },

    verDetalle: function(plan) {
      try {
        this.loading.verDetalle = plan.id
        
        this.$logger.log('🔍 Consultando detalle del plan:', plan)
        
        // Mostrar modal con el detalle del plan organizado en categorías
        this.$swal.fire({
          title: plan.descripcion,
          html: this.construirDetalleModal(plan),
          width: '90%',
          maxWidth: '1200px',
          showConfirmButton: true,
          confirmButtonText: 'Cerrar',
          confirmButtonClass: 'btn btn-primary',
          allowOutsideClick: true,
          allowEscapeKey: true,
          customClass: {
            container: 'swal-detalle-plan',
            popup: 'swal-popup-detalle',
            content: 'swal-content-detalle'
          }
        })
        
      } catch (error) {
        this.$logger.error('❌ Error al mostrar detalle del plan:', error)
        this.$swal.fire({
          title: 'Error',
          text: 'No se pudo mostrar el detalle del plan',
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-danger'
        })
      } finally {
        this.loading.verDetalle = null
      }
    },

    construirDetalleModal: function(plan) {
      return `
        <div class="plan-detalle-container">
          <!-- Header Principal -->
          <div class="plan-detalle-header">
            <div class="plan-icon-header">
              <i class="nc-icon nc-credit-card"></i>
            </div>
            <div class="plan-info-header">
              <div class="plan-precio-principal">$${this.formatearPrecioExacto(plan.valor)}</div>
              ${plan.indicadorMatricula ? `<div class="plan-matricula-info">+ Matrícula: $${this.formatearPrecioExacto(plan.valorMatricula)}</div>` : ''}
              <div class="plan-estado-badge">
                <span class="badge ${plan.activo ? 'badge-success' : 'badge-secondary'}">
                  <i class="nc-icon ${plan.activo ? 'nc-check-2' : 'nc-simple-remove'}"></i>
                  ${plan.activo ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
          </div>

          <!-- Información Básica -->
          <div class="detalle-seccion">
            <div class="seccion-titulo">
              <i class="nc-icon nc-paper mr-2"></i>
              <h5>Información Básica</h5>
            </div>
            <div class="tabla-detalle">
              <table class="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td class="etiqueta-dato"><strong>ID del Plan:</strong></td>
                    <td class="valor-dato">${plan.id}</td>
                    <td class="etiqueta-dato"><strong>Familia:</strong></td>
                    <td class="valor-dato">${plan.nombreFamilia || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td class="etiqueta-dato"><strong>SubFamilia:</strong></td>
                    <td class="valor-dato">${plan.nombreSubFamilia || 'N/A'}</td>
                    <td class="etiqueta-dato"><strong>Convenio:</strong></td>
                    <td class="valor-dato">${plan.convenio?.glosa || plan.convenio?.descripcion || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td class="etiqueta-dato"><strong>Tipo IVA:</strong></td>
                    <td class="valor-dato">${plan.tipoIVA || 'AFECTO'}</td>
                    <td class="etiqueta-dato"><strong>Descuento:</strong></td>
                    <td class="valor-dato">${plan.porcentajeDescuento || 0}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Duración -->
          <div class="detalle-seccion">
            <div class="seccion-titulo">
              <i class="nc-icon nc-time-alarm mr-2"></i>
              <h5>Duración</h5>
            </div>
            <div class="tabla-detalle">
              <table class="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td class="etiqueta-dato"><strong>Duración:</strong></td>
                    <td class="valor-dato">${plan.duracion} ${plan.unidadDuracionPlan?.glosa || plan.unidadDuracionPlan?.descripcion || 'unidades'}</td>
                    <td class="etiqueta-dato"><strong>Mes Calendario:</strong></td>
                    <td class="valor-dato">
                      <span class="badge ${plan.indicadorMesCalendario ? 'badge-success' : 'badge-warning'}">
                        ${plan.indicadorMesCalendario ? 'Habilitado' : 'Deshabilitado'}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td class="etiqueta-dato"><strong>Días por Mes (No Cal.):</strong></td>
                    <td class="valor-dato">${plan.diasMesNoCalendario || 30} días</td>
                    <td class="etiqueta-dato"><strong>Días para Congelar:</strong></td>
                    <td class="valor-dato">${plan.diasPermitidoCongelar || 0} días</td>
                  </tr>
                  ${plan.indicadorVigenciaTemporal ? `
                    <tr>
                      <td class="etiqueta-dato"><strong>Vigencia Desde:</strong></td>
                      <td class="valor-dato">${this.formatearFecha(plan.fechaInicioVigencia)}</td>
                      <td class="etiqueta-dato"><strong>Vigencia Hasta:</strong></td>
                      <td class="valor-dato">${this.formatearFecha(plan.fechaTerminoVigencia)}</td>
                    </tr>
                  ` : ''}
                </tbody>
              </table>
            </div>
          </div>

          <!-- Configuración -->
          <div class="detalle-seccion">
            <div class="seccion-titulo">
              <i class="nc-icon nc-settings-gear-65 mr-2"></i>
              <h5>Configuración</h5>
            </div>
            <div class="configuracion-grid">
              <div class="config-item">
                <div class="config-icon ${plan.indicadorMatricula ? 'enabled' : 'disabled'}">
                  <i class="nc-icon ${plan.indicadorMatricula ? 'nc-check-2' : 'nc-simple-remove'}"></i>
                </div>
                <div class="config-texto">
                  <span class="config-label">Matrícula</span>
                  <span class="config-desc">${plan.indicadorMatricula ? 'Requerida' : 'No requerida'}</span>
                </div>
              </div>

              <div class="config-item">
                <div class="config-icon ${plan.indicadorUsoSesiones ? 'enabled' : 'disabled'}">
                  <i class="nc-icon ${plan.indicadorUsoSesiones ? 'nc-check-2' : 'nc-simple-remove'}"></i>
                </div>
                <div class="config-texto">
                  <span class="config-label">Control por Sesiones</span>
                  <span class="config-desc">${plan.indicadorUsoSesiones ? `Límite: ${plan.sesionesLimite} sesiones` : 'Sin límite'}</span>
                </div>
              </div>

              <div class="config-item">
                <div class="config-icon ${plan.multiSede ? 'enabled' : 'disabled'}">
                  <i class="nc-icon ${plan.multiSede ? 'nc-check-2' : 'nc-simple-remove'}"></i>
                </div>
                <div class="config-texto">
                  <span class="config-label">MultiSede</span>
                  <span class="config-desc">${plan.multiSede ? 'Acceso a múltiples sedes' : 'Una sede únicamente'}</span>
                </div>
              </div>

              <div class="config-item">
                <div class="config-icon ${plan.habilitarVenta ? 'enabled' : 'disabled'}">
                  <i class="nc-icon ${plan.habilitarVenta ? 'nc-check-2' : 'nc-simple-remove'}"></i>
                </div>
                <div class="config-texto">
                  <span class="config-label">Venta</span>
                  <span class="config-desc">${plan.habilitarVenta ? 'Habilitada' : 'Deshabilitada'}</span>
                </div>
              </div>

              <div class="config-item">
                <div class="config-icon ${plan.habilitarVentaMobile ? 'enabled' : 'disabled'}">
                  <i class="nc-icon ${plan.habilitarVentaMobile ? 'nc-check-2' : 'nc-simple-remove'}"></i>
                </div>
                <div class="config-texto">
                  <span class="config-label">Venta Mobile</span>
                  <span class="config-desc">${plan.habilitarVentaMobile ? 'Disponible' : 'No disponible'}</span>
                </div>
              </div>

              <div class="config-item">
                <div class="config-icon ${plan.esSuscripcion ? 'enabled' : 'disabled'}">
                  <i class="nc-icon ${plan.esSuscripcion ? 'nc-check-2' : 'nc-simple-remove'}"></i>
                </div>
                <div class="config-texto">
                  <span class="config-label">Suscripción</span>
                  <span class="config-desc">${plan.esSuscripcion ? 'Plan de suscripción' : 'Plan tradicional'}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Turno -->
          ${plan.turno ? `
            <div class="detalle-seccion">
              <div class="seccion-titulo">
                <i class="nc-icon nc-calendar-60 mr-2"></i>
                <h5>Turno Asignado</h5>
              </div>
              <div class="turno-info-detalle">
                <div class="turno-nombre">
                  <strong>${plan.turno.descripcionTurno}</strong>
                  <span class="badge badge-info ml-2">ID: ${plan.turno.idTurno}</span>
                </div>
                <div class="tabla-detalle mt-3">
                  <table class="table table-sm table-hover">
                    <thead class="thead-light">
                      <tr>
                        <th>Día</th>
                        <th>Horario</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${this.construirFilasHorarios(plan.turno)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ` : `
            <div class="detalle-seccion">
              <div class="seccion-titulo">
                <i class="nc-icon nc-calendar-60 mr-2"></i>
                <h5>Turno Asignado</h5>
              </div>
              <div class="sin-turno">
                <i class="nc-icon nc-time-alarm text-muted" style="font-size: 2rem;"></i>
                <p class="text-muted mt-2">No hay turno asignado a este plan</p>
              </div>
            </div>
          `}

          <!-- Información Adicional -->
          <div class="detalle-seccion">
            <div class="seccion-titulo">
              <i class="nc-icon nc-single-02 mr-2"></i>
              <h5>Información Adicional</h5>
            </div>
            <div class="tabla-detalle">
              <table class="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td class="etiqueta-dato"><strong>Permite Renovar:</strong></td>
                    <td class="valor-dato">
                      <span class="badge ${plan.indicadorPermiteRenovar ? 'badge-success' : 'badge-secondary'}">
                        ${plan.indicadorPermiteRenovar ? 'Sí' : 'No'}
                      </span>
                    </td>
                    <td class="etiqueta-dato"><strong>Genera Factura:</strong></td>
                    <td class="valor-dato">
                      <span class="badge ${plan.indicadorFactura ? 'badge-primary' : 'badge-light'}">
                        ${plan.indicadorFactura ? 'Automática' : 'Manual'}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td class="etiqueta-dato"><strong>Fecha Creación:</strong></td>
                    <td class="valor-dato">${this.formatearFecha(plan.createdAt)}</td>
                    <td class="etiqueta-dato"><strong>Última Modificación:</strong></td>
                    <td class="valor-dato">${this.formatearFecha(plan.updatedAt)}</td>
                  </tr>
                  ${plan.nombreUsuarioCreacion ? `
                    <tr>
                      <td class="etiqueta-dato"><strong>Creado por:</strong></td>
                      <td class="valor-dato" colspan="3">${plan.nombreUsuarioCreacion}</td>
                    </tr>
                  ` : ''}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      `
    },

    construirFilasHorarios: function(turno) {
      const dias = [
        { nombre: 'Lunes', autorizado: turno.lunesAutorizado, inicio: turno.lunesHoraInicio, termino: turno.lunesHoraTermino },
        { nombre: 'Martes', autorizado: turno.martesAutorizado, inicio: turno.martesHoraInicio, termino: turno.martesHoraTermino },
        { nombre: 'Miércoles', autorizado: turno.miercolesAutorizado, inicio: turno.miercolesHoraInicio, termino: turno.miercolesHoraTermino },
        { nombre: 'Jueves', autorizado: turno.juevesAutorizado, inicio: turno.juevesHoraInicio, termino: turno.juevesHoraTermino },
        { nombre: 'Viernes', autorizado: turno.viernesAutorizado, inicio: turno.viernesHoraInicio, termino: turno.viernesHoraTermino },
        { nombre: 'Sábado', autorizado: turno.sabadoAutorizado, inicio: turno.sabadoHoraInicio, termino: turno.sabadoHoraTermino },
        { nombre: 'Domingo', autorizado: turno.domingoAutorizado, inicio: turno.domingoHoraInicio, termino: turno.domingoHoraTermino }
      ]

      return dias.map(dia => {
        if (dia.autorizado) {
          return `
            <tr>
              <td><strong>${dia.nombre}</strong></td>
              <td>${this.formatearHora(dia.inicio)} - ${this.formatearHora(dia.termino)}</td>
              <td><span class="badge badge-success">Activo</span></td>
            </tr>
          `
        } else {
          return `
            <tr class="table-light">
              <td class="text-muted">${dia.nombre}</td>
              <td class="text-muted">No disponible</td>
              <td><span class="badge badge-light">Inactivo</span></td>
            </tr>
          `
        }
      }).join('')
    },

    formatearHora: function(hora) {
      if (!hora) return 'N/A'
      // Si la hora ya está en formato HH:MM, devolverla tal como está
      if (typeof hora === 'string' && hora.includes(':')) {
        return hora
      }
      // Si es un número, convertirlo a formato HH:MM
      if (typeof hora === 'number') {
        const horas = Math.floor(hora / 100)
        const minutos = hora % 100
        return `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}`
      }
      return hora.toString()
    },

    formatearFecha: function(fecha) {
      if (!fecha) return 'N/A'
      try {
        return new Date(fecha).toLocaleString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }).replace(',', ' -')
      } catch (error) {
        return fecha.toString()
      }
    },

    exportarExcel: async function() {
      // Mostrar modal de funcionalidad en desarrollo
      this.$swal.fire({
        icon: 'info',
        title: 'Funcionalidad en Desarrollo',
        html: `
          <div class="text-center">
            <i class="nc-icon nc-ruler-pencil text-info mb-3" style="font-size: 48px;"></i>
            <p class="mb-2">La función de exportación de planes está en desarrollo.</p>
            <p><strong>¡Pronto estará disponible en una nueva versión!</strong></p>
          </div>
        `,
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#667eea',
        showCancelButton: false
      })
      
      /* FUNCIONALIDAD COMENTADA - EN DESARROLLO
      try {
        this.loading.exportar = true
        
        // Verificar que hay planes para exportar
        if (!this.planes || this.planes.length === 0) {
          this.$swal.fire({
            icon: 'warning',
            title: 'Sin datos para exportar',
            text: 'No hay planes cargados para exportar.',
            confirmButtonColor: '#6bd098'
          })
          this.loading.exportar = false
          return
        }
        
        this.$swal.fire({
          title: 'Generando archivo Excel...',
          text: 'Preparando datos de planes para exportación',
          showConfirmButton: false,
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading()
          }
        })

        // Importar dinámicamente ExcelJS
        const ExcelJS = await import('exceljs')
        const workbook = new ExcelJS.Workbook()
        
        // Metadatos del libro
        workbook.creator = 'CentralGym'
        workbook.lastModifiedBy = 'Sistema CentralGym'
        workbook.created = new Date()
        workbook.modified = new Date()
        
        // Crear hoja principal
        const worksheet = workbook.addWorksheet('Planes', {
          properties: { tabColor: { argb: '6BD098' } }
        })

        // Definir columnas
        worksheet.columns = [
          { header: 'ID', key: 'id', width: 10 },
          { header: 'Nombre del Plan', key: 'nombre', width: 35 },
          { header: 'Familia', key: 'familia', width: 20 },
          { header: 'SubFamilia', key: 'subfamilia', width: 20 },
          { header: 'Duración', key: 'duracion', width: 12 },
          { header: 'Valor', key: 'valor', width: 15 },
          { header: 'Descuento (%)', key: 'descuento', width: 12 },
          { header: 'Estado', key: 'estado', width: 10 },
          { header: 'Multisede', key: 'multisede', width: 10 },
          { header: 'Temporal', key: 'temporal', width: 10 },
          { header: 'Vigencia Desde', key: 'vigenciaDesde', width: 15 },
          { header: 'Vigencia Hasta', key: 'vigenciaHasta', width: 15 },
          { header: 'Sesiones', key: 'sesiones', width: 10 },
          { header: 'Venta Habilitada', key: 'ventaHabilitada', width: 15 },
          { header: 'Sucursales', key: 'sucursales', width: 30 },
          { header: 'Turno', key: 'turno', width: 15 },
          { header: 'Fecha Creación', key: 'fechaCreacion', width: 15 }
        ]

        // Estilo de encabezados
        worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } }
        worksheet.getRow(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF6BD098' }
        }
        worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' }
        worksheet.getRow(1).height = 20

        // Agregar datos
        this.planes.forEach((plan, index) => {
          const row = worksheet.addRow({
            id: plan._id || plan.id || '',
            nombre: plan.nombre || '',
            familia: plan.familia?.nombre || plan.familia || 'Sin familia',
            subfamilia: plan.subFamilia?.nombre || plan.subFamilia || 'Sin subfamilia',
            duracion: plan.duracion || 0,
            valor: plan.valor || 0,
            descuento: plan.porcentajeDescuento || 0,
            estado: plan.activo ? 'Activo' : 'Inactivo',
            multisede: plan.multiSede ? 'Sí' : 'No',
            temporal: plan.planTemporal ? 'Sí' : 'No',
            vigenciaDesde: plan.vigenciaInicio ? this.formatearFecha(plan.vigenciaInicio) : '',
            vigenciaHasta: plan.vigenciaFin ? this.formatearFecha(plan.vigenciaFin) : '',
            sesiones: plan.usoSesiones ? (plan.sesionesTotales || 0) : 'N/A',
            ventaHabilitada: plan.habilitarVenta ? 'Sí' : 'No',
            sucursales: Array.isArray(plan.sucursalesHabilitadas) ? plan.sucursalesHabilitadas.join(', ') : 'Todas',
            turno: plan.turno?.nombre || plan.turno || 'Sin turno',
            fechaCreacion: plan.createdAt ? this.formatearFecha(plan.createdAt) : ''
          })

          // Alternar colores de filas
          if (index % 2 === 1) {
            row.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF5F5F5' }
            }
          }

          // Colorear estado
          const estadoCell = row.getCell('estado')
          if (plan.activo) {
            estadoCell.font = { color: { argb: 'FF28A745' }, bold: true }
          } else {
            estadoCell.font = { color: { argb: 'FFDC3545' }, bold: true }
          }

          // Formato de moneda para valor
          row.getCell('valor').numFmt = '$#,##0'
        })

        // Auto ajustar anchos (opcional, puede ser lento con muchos datos)
        worksheet.columns.forEach(column => {
          column.width = Math.min(column.width || 15, 50)
        })

        // Agregar filtros automáticos
        worksheet.autoFilter = {
          from: 'A1',
          to: `Q${this.planes.length + 1}`
        }

        // Congelar panel superior
        worksheet.views = [
          { state: 'frozen', xSplit: 0, ySplit: 1 }
        ]

        // Agregar hoja de resumen
        const summarySheet = workbook.addWorksheet('Resumen', {
          properties: { tabColor: { argb: 'FF667EEA' } }
        })

        summarySheet.columns = [
          { header: 'Métrica', key: 'metrica', width: 30 },
          { header: 'Valor', key: 'valor', width: 20 }
        ]

        // Calcular estadísticas
        const totalPlanes = this.planes.length
        const planesActivos = this.planes.filter(p => p.activo).length
        const planesInactivos = totalPlanes - planesActivos
        const planesMultisede = this.planes.filter(p => p.multiSede).length
        const planesTemporales = this.planes.filter(p => p.planTemporal).length
        const valorPromedio = this.planes.reduce((sum, p) => sum + (p.valor || 0), 0) / totalPlanes || 0

        summarySheet.addRows([
          { metrica: 'Total de Planes', valor: totalPlanes },
          { metrica: 'Planes Activos', valor: planesActivos },
          { metrica: 'Planes Inactivos', valor: planesInactivos },
          { metrica: 'Planes Multisede', valor: planesMultisede },
          { metrica: 'Planes Temporales', valor: planesTemporales },
          { metrica: 'Valor Promedio', valor: Math.round(valorPromedio) }
        ])

        // Estilo para hoja de resumen
        summarySheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } }
        summarySheet.getRow(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF667EEA' }
        }

        // Generar y descargar archivo
        const buffer = await workbook.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        
        const fecha = new Date().toISOString().slice(0, 10).replace(/-/g, '')
        link.download = `Planes_CentralGym_${fecha}.xlsx`
        link.click()
        
        window.URL.revokeObjectURL(url)

        this.$swal.close()
        
        this.$swal.fire({
          icon: 'success',
          title: '¡Exportación Exitosa!',
          html: `
            <div class="text-left">
              <p><strong>📊 Archivo Excel generado correctamente</strong></p>
              <p><strong>📁 Archivo:</strong> Planes_CentralGym_${fecha}.xlsx</p>
              <p><strong>📈 Planes exportados:</strong> ${totalPlanes}</p>
              <p><strong>✅ Planes activos:</strong> ${planesActivos}</p>
              <p><strong>💼 Incluye:</strong> Hoja de datos completa y resumen estadístico</p>
            </div>
          `,
          confirmButtonColor: '#6bd098',
          confirmButtonText: 'Excelente'
        })
        
        this.$logger.log('✅ Exportación Excel completada:', {
          archivo: `Planes_CentralGym_${fecha}.xlsx`,
          totalPlanes: totalPlanes,
          hojas: ['Planes', 'Resumen']
        })
        
        this.loading.exportar = false

      } catch (error) {
        this.$logger.error('Error al exportar Excel:', error)
        this.$swal.fire({
          icon: 'error',
          title: 'Error al exportar',
          text: 'No se pudo generar el archivo Excel. Por favor, intenta nuevamente.',
          confirmButtonColor: '#6bd098'
        })
        this.loading.exportar = false
      }
      */
    },

    // Métodos para el modal de creación
    mostrarModalCreacion: function() {
      var modalElement = this.$refs.modalCrearPlan
      if (modalElement) {
        if (window.bootstrap && window.bootstrap.Modal) {
          var modal = new window.bootstrap.Modal(modalElement)
          modal.show()
        } else {
          modalElement.style.display = 'block'
          modalElement.classList.add('show')
          document.body.classList.add('modal-open')
          
          var backdrop = document.createElement('div')
          backdrop.className = 'modal-backdrop fade show'
          backdrop.id = 'modal-backdrop-crear-plan'
          document.body.appendChild(backdrop)
        }
      }
    },

    cerrarModalCreacion: function() {
      var modalElement = this.$refs.modalCrearPlan
      if (modalElement) {
        if (window.bootstrap && window.bootstrap.Modal) {
          var modalInstance = window.bootstrap.Modal.getInstance(modalElement)
          if (modalInstance) {
            modalInstance.hide()
          }
        } else {
          modalElement.style.display = 'none'
          modalElement.classList.remove('show')
          document.body.classList.remove('modal-open')
          
          var backdrop = document.getElementById('modal-backdrop-crear-plan')
          if (backdrop) {
            backdrop.remove()
          }
        }
      }
      
      // Limpiar estado del modal
      this.modoEdicion = false
      this.planSeleccionado = null
      this.limpiarFormulario()
    },

    limpiarFormulario: function() {
      this.formPlan = {
        familia: '',
        subfamilia: '',
        descripcion: '',
        valor: null,
        indicadorMatricula: false,
        valorMatricula: null,
        porcentajeDescuento: 0,
        indicadorFactura: false,
        tipoIVA: 'AFECTO',
        duracion: 1,
        unidadDuracionPlan: '',
        indicadorMesCalendario: true,
        diasMesNoCalendario: 0,
        diasPermitidoCongelar: 0,
        indicadorUsoSesiones: false,
        sesionesLimite: 0,
        descuentoSesionesXDia: true,
        multiSede: false,
        sucursalesHabilitadas: [],
        indicadorVigenciaTemporal: false,
        fechaInicioVigencia: '',
        fechaTerminoVigencia: '',
        indicadorPermiteRenovar: true,
        expiracionRenovacion: 30,
        unidadExpiracionRenovacion: '',
        habilitarVenta: true,
        habilitarVentaMobile: true,
        esSuscripcion: false,
        convenio: '',
        turnoSeleccionado: null,
        informacionAdicional: {
          limitarPuntoAcceso: false,
          puntoAccesoPermitidos: [],
          beneficiosAdicionales: []
        }
      }
      
      this.nuevoPuntoAcceso = {
        idAcceso: '',
        descontarSesion: false
      }
      this.nuevoBeneficio = ''
      
      // Limpiar errores de validación visual
      this.limpiarErroresVisuales()
    },

    onFamiliaFormChange: function() {
      this.formPlan.subfamilia = ''
    },

    agregarPuntoAcceso: function() {
      if (this.nuevoPuntoAcceso.idAcceso.trim()) {
        this.formPlan.informacionAdicional.puntoAccesoPermitidos.push({
          idAcceso: this.nuevoPuntoAcceso.idAcceso.trim(),
          descontarSesion: this.nuevoPuntoAcceso.descontarSesion
        })
        this.nuevoPuntoAcceso = {
          idAcceso: '',
          descontarSesion: false
        }
      }
    },

    removerPuntoAcceso: function(index) {
      this.formPlan.informacionAdicional.puntoAccesoPermitidos.splice(index, 1)
    },

    agregarBeneficio: function() {
      if (this.nuevoBeneficio.trim()) {
        this.formPlan.informacionAdicional.beneficiosAdicionales.push(this.nuevoBeneficio.trim())
        this.nuevoBeneficio = ''
      }
    },

    removerBeneficio: function(index) {
      this.formPlan.informacionAdicional.beneficiosAdicionales.splice(index, 1)
    },

    // Métodos para gestión de sucursales
    isSucursalSelected: function(sucursal) {
      return this.formPlan.sucursalesHabilitadas.some(function(s) {
        return s._id === sucursal._id
      })
    },

    toggleSucursal: function(sucursal) {
      if (this.loading.guardar) return
      
      const index = this.formPlan.sucursalesHabilitadas.findIndex(function(s) {
        return s._id === sucursal._id
      })
      
      if (index === -1) {
        // Agregar sucursal con la estructura correcta para el backend
        this.formPlan.sucursalesHabilitadas.push({
          _id: sucursal._id,
          nombreSucursal: sucursal.nombreSucursal,
          type: sucursal.type || 'FISICA'
        })
        this.$logger.log('✅ Sucursal agregada:', sucursal.nombreSucursal)
      } else {
        // Remover sucursal
        this.formPlan.sucursalesHabilitadas.splice(index, 1)
        this.$logger.log('❌ Sucursal removida:', sucursal.nombreSucursal)
      }
      
      this.$logger.log('🏢 Sucursales habilitadas actuales:', this.formPlan.sucursalesHabilitadas.length)
    },

    seleccionarTodasSucursales: function() {
      if (this.loading.guardar) return
      
      this.formPlan.sucursalesHabilitadas = this.sucursalesUsuario.map(function(sucursal) {
        return {
          _id: sucursal._id,
          nombreSucursal: sucursal.nombreSucursal,
          type: sucursal.type || 'FISICA'
        }
      })
      
      this.$logger.log('✅ Todas las sucursales seleccionadas:', this.formPlan.sucursalesHabilitadas.length)
      
      this.$swal.fire({
        title: 'Sucursales Seleccionadas',
        text: 'Se han seleccionado todas las sucursales disponibles (' + this.formPlan.sucursalesHabilitadas.length + ')',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      })
    },

    limpiarSucursalesSeleccionadas: function() {
      if (this.loading.guardar) return
      
      this.formPlan.sucursalesHabilitadas = []
      
      this.$logger.log('🧹 Sucursales seleccionadas limpiadas')
      
      this.$swal.fire({
        title: 'Selección Limpiada',
        text: 'Se ha limpiado la selección de sucursales',
        icon: 'info',
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      })
    },

    removerSucursal: function(sucursalId) {
      if (this.loading.guardar) return
      
      const index = this.formPlan.sucursalesHabilitadas.findIndex(function(s) {
        return s._id === sucursalId
      })
      
      if (index !== -1) {
        const sucursalRemovida = this.formPlan.sucursalesHabilitadas[index]
        this.formPlan.sucursalesHabilitadas.splice(index, 1)
        this.$logger.log('❌ Sucursal removida:', sucursalRemovida.nombreSucursal)
      }
    },

    getSucursalIcon: function(type) {
      switch(type) {
        case 'FISICA':
          return 'nc-icon nc-shop'
        case 'VIRTUAL':
          return 'nc-icon nc-world-2'
        default:
          return 'nc-icon nc-pin-3'
      }
    },

    validarFormulario: function() {
      var errores = []
      
      if (!this.formPlan.familia) {
        errores.push('Debe seleccionar una familia')
      }
      
      if (!this.formPlan.subfamilia) {
        errores.push('Debe seleccionar una subfamilia')
      }
      
      if (!this.formPlan.descripcion.trim()) {
        errores.push('La descripción es obligatoria')
      }
      
      // Validación del valor: debe ser un número >= 0 (se permite 0 para planes gratuitos)
      if (this.formPlan.valor === null || this.formPlan.valor === undefined) {
        errores.push('Debe especificar un valor para el plan (se permite 0 para planes gratuitos)')
      } else if (isNaN(this.formPlan.valor) || this.formPlan.valor < 0) {
        errores.push('El valor debe ser un número mayor o igual a 0 (cero permitido)')
      }
      
      // Validación del valor de matrícula si está habilitada
      if (this.formPlan.indicadorMatricula) {
        if (this.formPlan.valorMatricula === null || this.formPlan.valorMatricula === undefined) {
          errores.push('Si requiere matrícula, debe especificar un valor (se permite 0)')
        } else if (isNaN(this.formPlan.valorMatricula) || this.formPlan.valorMatricula < 0) {
          errores.push('El valor de matrícula debe ser un número mayor o igual a 0 (cero permitido)')
        }
      }
      
      if (!this.formPlan.unidadDuracionPlan) {
        errores.push('Debe seleccionar la unidad de duración')
      }
      
      if (this.formPlan.indicadorUsoSesiones && (!this.formPlan.sesionesLimite || this.formPlan.sesionesLimite <= 0)) {
        errores.push('Si usa sesiones, debe especificar el límite')
      }
      
      if (this.formPlan.indicadorVigenciaTemporal) {
        if (!this.formPlan.fechaInicioVigencia || !this.formPlan.fechaTerminoVigencia) {
          errores.push('Si tiene vigencia temporal, debe especificar las fechas')
        }
      }
      
      // Validaciones de renovación - con reglas específicas por tipo de unidad
      if (this.formPlan.indicadorPermiteRenovar) {
        if (!this.formPlan.unidadExpiracionRenovacion) {
          errores.push('Renovación - Debe seleccionar la unidad de expiración para renovar')
        } else {
          // Obtener información de la unidad seleccionada
          const infoUnidad = this.obtenerInfoUnidadActual()
          
          // Validaciones específicas según el tipo de unidad
          if (infoUnidad.tipo === 'indefinida') {
            // INDEFINIDA (I): El valor DEBE ser 0
            if (this.formPlan.expiracionRenovacion !== 0) {
              errores.push('Renovación - Con unidad INDEFINIDA, el valor de "Expiración para Renovar" debe ser 0')
            }
          } else if (infoUnidad.tipo === 'dias' || infoUnidad.tipo === 'meses') {
            // DIAS (D) o MESES (M): El valor NO puede ser 0
            if (this.formPlan.expiracionRenovacion === null || 
                this.formPlan.expiracionRenovacion === undefined || 
                this.formPlan.expiracionRenovacion === 0) {
              const tipoTexto = infoUnidad.tipo === 'dias' ? 'DÍAS' : 'MESES'
              errores.push(`Renovación - Con unidad ${tipoTexto}, el valor de "Expiración para Renovar" no puede ser 0`)
            }
            
            // Validar que sea positivo
            if (this.formPlan.expiracionRenovacion < 0) {
              errores.push('Renovación - El valor de "Expiración para Renovar" debe ser un número positivo')
            }
          } else {
            // Para otros tipos de unidad, validación básica
            if (this.formPlan.expiracionRenovacion === null || 
                this.formPlan.expiracionRenovacion === undefined || 
                this.formPlan.expiracionRenovacion < 0) {
              errores.push('Renovación - El valor de "Expiración para Renovar" debe ser mayor o igual a 0')
            }
          }
        }
      }
      
      if (!this.formPlan.convenio) {
        errores.push('Debe seleccionar el tipo de convenio')
      }
      
      if (!this.formPlan.turnoSeleccionado) {
        errores.push('Debe seleccionar un turno')
      }
      
      // Nota: La validación de sucursales ya no es obligatoria - si no se selecciona ninguna, 
      // el plan estará disponible en todas las sucursales por defecto
      
      return errores
    },

    async guardarPlan() {
      var errores = this.validarFormulario()
      
      if (errores.length > 0) {
        this.$swal.fire({
          title: 'Errores en el Formulario',
          html: '<ul style="text-align: left;">' + errores.map(function(error) { return '<li>' + error + '</li>' }).join('') + '</ul>',
          icon: 'warning',
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-warning'
        })
        return
      }

      try {
        this.loading.guardar = true

        this.$logger.log('📝 Creando plan:', this.formPlan)

        // Buscar el turno completo desde los catálogos
        var turnoCompleto = null
        if (this.formPlan.turnoSeleccionado) {
          var turnoEncontrado = this.catalogosCreacion.turnos.find(function(turno) {
            return turno._id === this.formPlan.turnoSeleccionado
          }.bind(this))
          
          if (turnoEncontrado) {
            turnoCompleto = {
              idTurno: turnoEncontrado.id,
              descripcionTurno: turnoEncontrado.descripcionTurno,
              lunesAutorizado: turnoEncontrado.lunesAutorizado,
              martesAutorizado: turnoEncontrado.martesAutorizado,
              miercolesAutorizado: turnoEncontrado.miercolesAutorizado,
              juevesAutorizado: turnoEncontrado.juevesAutorizado,
              viernesAutorizado: turnoEncontrado.viernesAutorizado,
              sabadoAutorizado: turnoEncontrado.sabadoAutorizado,
              domingoAutorizado: turnoEncontrado.domingoAutorizado,
              lunesHoraInicio: turnoEncontrado.lunesHoraInicio,
              lunesHoraTermino: turnoEncontrado.lunesHoraTermino,
              martesHoraInicio: turnoEncontrado.martesHoraInicio,
              martesHoraTermino: turnoEncontrado.martesHoraTermino,
              miercolesHoraInicio: turnoEncontrado.miercolesHoraInicio,
              miercolesHoraTermino: turnoEncontrado.miercolesHoraTermino,
              juevesHoraInicio: turnoEncontrado.juevesHoraInicio,
              juevesHoraTermino: turnoEncontrado.juevesHoraTermino,
              viernesHoraInicio: turnoEncontrado.viernesHoraInicio,
              viernesHoraTermino: turnoEncontrado.viernesHoraTermino,
              sabadoHoraInicio: turnoEncontrado.sabadoHoraInicio,
              sabadoHoraTermino: turnoEncontrado.sabadoHoraTermino,
              domingoHoraInicio: turnoEncontrado.domingoHoraInicio,
              domingoHoraTermino: turnoEncontrado.domingoHoraTermino
            }
          }
        }

        // Preparar sucursales habilitadas con objetos completos
        // IMPORTANTE: Siempre incluir sucursales seleccionadas, independientemente de multiSede
        var sucursalesHabilitadasCompletas = []
        if (this.formPlan.sucursalesHabilitadas.length > 0) {
          sucursalesHabilitadasCompletas = this.formPlan.sucursalesHabilitadas.map(function(sucursal) {
            return {
              _id: sucursal._id,
              nombreSucursal: sucursal.nombreSucursal || sucursal.nombre
            }
          })
          
          this.$logger.log('🏢 Sucursales preparadas para enviar:', sucursalesHabilitadasCompletas)
        } else {
          this.$logger.warn('⚠️ No hay sucursales seleccionadas - el plan podría no estar disponible para venta')
        }

        // Preparar datos para el API
        var planData = {
          familia: this.formPlan.familia,
          subfamilia: this.formPlan.subfamilia,
          descripcion: this.formPlan.descripcion.trim(),
          valor: this.formPlan.valor === 0 ? 0 : parseFloat(this.formPlan.valor) || 0,
          indicadorMatricula: this.formPlan.indicadorMatricula,
          valorMatricula: this.formPlan.indicadorMatricula ? (this.formPlan.valorMatricula === 0 ? 0 : parseFloat(this.formPlan.valorMatricula) || 0) : 0,
          porcentajeDescuento: parseFloat(this.formPlan.porcentajeDescuento) / 100,
          indicadorFactura: this.formPlan.indicadorFactura,
          tipoIVA: this.formPlan.tipoIVA,
          duracion: parseInt(this.formPlan.duracion),
          unidadDuracionPlan: this.formPlan.unidadDuracionPlan,
          indicadorMesCalendario: this.formPlan.indicadorMesCalendario,
          diasMesNoCalendario: this.formPlan.indicadorMesCalendario ? 0 : parseInt(this.formPlan.diasMesNoCalendario),
          diasPermitidoCongelar: parseInt(this.formPlan.diasPermitidoCongelar),
          indicadorUsoSesiones: this.formPlan.indicadorUsoSesiones,
          sesionesLimite: this.formPlan.indicadorUsoSesiones ? parseInt(this.formPlan.sesionesLimite) : 0,
          descuentoSesionesXDia: this.formPlan.descuentoSesionesXDia,
          multiSede: this.formPlan.multiSede,
          sucursalesHabilitadas: sucursalesHabilitadasCompletas,
          indicadorVigenciaTemporal: this.formPlan.indicadorVigenciaTemporal,
          fechaInicioVigencia: this.formPlan.indicadorVigenciaTemporal ? this.convertirFechaFormularioABackend(this.formPlan.fechaInicioVigencia) : '',
          fechaTerminoVigencia: this.formPlan.indicadorVigenciaTemporal ? this.convertirFechaFormularioABackend(this.formPlan.fechaTerminoVigencia) : '',
          indicadorPermiteRenovar: this.formPlan.indicadorPermiteRenovar,
          expiracionRenovacion: parseInt(this.formPlan.expiracionRenovacion),
          unidadExpiracionRenovacion: this.formPlan.unidadExpiracionRenovacion,
          habilitarVenta: this.formPlan.habilitarVenta,
          habilitarVentaMobile: this.formPlan.habilitarVentaMobile,
          esSuscripcion: this.formPlan.esSuscripcion,
          convenio: this.formPlan.convenio,
          turno: turnoCompleto,
          informacionAdicional: this.formPlan.informacionAdicional
        }

        this.$logger.log('📝 Datos del plan a enviar:', planData)
        this.$logger.log('💰 Validación de valores enviados:', {
          valorPlan: planData.valor,
          valorMatricula: planData.valorMatricula,
          tipoValorPlan: typeof planData.valor,
          tipoValorMatricula: typeof planData.valorMatricula
        })
        this.$logger.log('🔍 Turno completo construido:', turnoCompleto)
        this.$logger.log('🏢 Sucursales habilitadas construidas:', sucursalesHabilitadasCompletas)
        this.$logger.log('📅 Fechas enviadas al backend:', {
          formulario_inicio: this.formPlan.fechaInicioVigencia,
          formulario_termino: this.formPlan.fechaTerminoVigencia,
          backend_inicio: planData.fechaInicioVigencia,
          backend_termino: planData.fechaTerminoVigencia,
          vigencia_temporal: planData.indicadorVigenciaTemporal
        })

        // Validar que el turno se haya encontrado correctamente
        if (!turnoCompleto) {
          this.$swal.fire({
            title: 'Error en Turno',
            text: 'No se pudo encontrar la información completa del turno seleccionado',
            icon: 'error',
            confirmButtonText: 'Entendido',
            confirmButtonClass: 'btn btn-danger'
          })
          return
        }

        var operacion = this.modoEdicion ? '/v1/planes/actualizar' : '/v1/planes/crear'
        var parameters = this.modoEdicion ? { id: this.planSeleccionado.id } : {}

        var response = await this.$CentralGymOPS_API({
          method: 'POST',
          url: '/',
          headers: {
            'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
          },
          data: {
            Header: { Operacion: operacion },
            Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
            Parameters: parameters,
            Body: planData
          }
        })

        this.$logger.log('📝 Respuesta del servidor:', response.data)

        // Verificar respuesta exitosa según la especificación
        if (response.data?.result?.code === 0 && response.data?.result?.httpCode === 200) {
          var planCreado = response.data?.dataOut
          
          this.$swal.fire({
            title: this.modoEdicion ? '¡Plan Actualizado Exitosamente!' : '¡Plan Creado Exitosamente!',
            html: '<div class="text-center">' +
              '<i class="nc-icon nc-check-2" style="font-size: 3rem; color: #6bd098; margin-bottom: 15px;"></i>' +
              '<h5 style="color: #28a745; margin-bottom: 15px;">' + (this.modoEdicion ? '¡Plan Actualizado!' : '¡Nuevo Plan Disponible!') + '</h5>' +
              '<div class="alert alert-success text-left" style="margin: 15px 0;">' +
                '<strong>✅ Plan ' + (this.modoEdicion ? 'actualizado' : 'creado') + ' exitosamente:</strong>' +
                '<ul style="margin: 5px 0 0 20px;">' +
                  '<li><strong>ID:</strong> ' + planCreado.id + '</li>' +
                  '<li><strong>Descripción:</strong> ' + planCreado.descripcion + '</li>' +
                  '<li><strong>Familia:</strong> ' + planCreado.nombreFamilia + ' (ID: ' + planCreado.idFamilia + ')</li>' +
                  '<li><strong>SubFamilia:</strong> ' + planCreado.nombreSubFamilia + ' (ID: ' + planCreado.idSubFamilia + ')</li>' +
                  '<li><strong>Valor:</strong> $' + this.formatearPrecioExacto(planCreado.valor) + '</li>' +
                  '<li><strong>Duración:</strong> ' + planCreado.duracion + ' ' + (planCreado.unidadDuracionPlan?.glosa || 'N/A') + '</li>' +
                  '<li><strong>Turno:</strong> ' + (planCreado.turno?.descripcionTurno || 'N/A') + '</li>' +
                '</ul>' +
              '</div>' +
              '<div style="margin-top: 15px; padding: 10px; background: rgba(40, 167, 69, 0.1); border-radius: 4px;">' +
                '<small class="text-muted">' +
                  '<strong>💡 Información adicional:</strong><br>' +
                  '• Estado: ' + (planCreado.activo ? 'Activo' : 'Inactivo') + '<br>' +
                  '• Venta habilitada: ' + (planCreado.habilitarVenta ? 'Sí' : 'No') + '<br>' +
                  '• MultiSede: ' + (planCreado.multiSede ? 'Sí' : 'No') + '<br>' +
                  (this.modoEdicion ? '• Actualizado por: ' : '• Creado por: ') + (planCreado.nombreUsuarioCreacion || 'N/A') +
                  (planCreado.fechaActualizacionFormatted ? '<br>• Última actualización: ' + planCreado.fechaActualizacionFormatted : '') +
                '</small>' +
              '</div>' +
              '</div>',
            icon: 'success',
            showConfirmButton: true,
            confirmButtonText: 'Entendido',
            confirmButtonClass: 'btn btn-success',
            allowOutsideClick: false,
            allowEscapeKey: false
          })

          this.cerrarModalCreacion()
          await this.cargarPlanes()
          this.$emit('refresh')
          
        } else {
          throw new Error(response.data?.result?.message || 'Error desconocido del servidor')
        }

      } catch (error) {
        this.$logger.error('❌ Error al crear plan:', error)
        
        var errorMessage = 'No se pudo crear el plan'
        var errorTitle = 'Error'
        
        if (error.response?.status === 500) {
          // Manejo específico de errores del servidor
          var errorString = error.response.data?.result?.errors || ''
          var errorMessage = error.response.data?.result?.message || ''
          
          // Error de plan duplicado - detectar por mensaje o error
          if ((errorString.includes('duplicate key error') && errorString.includes('descripcion')) || 
              (errorMessage.includes('Error al crear plan') && errorString.includes('E11000 duplicate key error'))) {
            this.$swal.fire({
              title: 'Plan Duplicado',
              html: '<div class="text-center">' +
                '<i class="nc-icon nc-simple-remove" style="font-size: 3rem; color: #f39c12; margin-bottom: 15px;"></i>' +
                '<h5 style="color: #856404; margin-bottom: 15px;">¡Plan Ya Existe!</h5>' +
                '<div class="alert alert-warning text-left" style="margin: 15px 0;">' +
                  '<strong>⚠️ Conflicto de Duplicación:</strong>' +
                  '<p style="margin: 8px 0;">Ya existe un plan con esta descripción en la misma familia y subfamilia.</p>' +
                '</div>' +
                '<div style="margin: 15px 0; padding: 15px; background: rgba(243, 156, 18, 0.1); border-radius: 8px; border-left: 4px solid #f39c12;">' +
                  '<p style="margin: 0; font-size: 0.9rem; text-align: left;">' +
                    '<strong>📋 Detalles del conflicto:</strong><br>' +
                    '• <strong>Familia:</strong> ' + this.getFamiliaSeleccionadaNombreForm() + '<br>' +
                    '• <strong>SubFamilia:</strong> ' + this.getSubFamiliaSeleccionadaNombreForm() + '<br>' +
                    '• <strong>Descripción:</strong> "' + this.formPlan.descripcion + '"<br>' +
                    '• <strong>Restricción:</strong> Descripciones únicas por familia/subfamilia' +
                  '</p>' +
                '</div>' +
                '<div style="margin-top: 15px; padding: 10px; background: rgba(40, 167, 69, 0.1); border-radius: 4px;">' +
                  '<small class="text-muted">' +
                    '<strong>💡 Solución:</strong> Modifica la descripción del plan para hacerla única o busca el plan existente para editarlo.' +
                  '</small>' +
                '</div>' +
                '</div>',
              icon: 'warning',
              confirmButtonText: 'Cambiar Descripción',
              confirmButtonClass: 'btn btn-warning',
              allowOutsideClick: false,
              allowEscapeKey: false
            }).then(function() {
              // Mantener el modal abierto y enfocar el campo de descripción
              var inputDescripcion = document.getElementById('descripcionPlan')
              if (inputDescripcion) {
                inputDescripcion.focus()
                inputDescripcion.select()
              }
            })
            return
          } else {
            // Otros errores del servidor
            errorMessage = errorMessage || 'Error interno del servidor al crear el plan'
            errorTitle = 'Error del Servidor'
          }
        } else if (error.response?.status === 400) {
          // Error de validación del backend - procesar errores específicos por campo
          const resultData = error.response.data?.result
          const erroresEspecificos = resultData?.errors || []
          const mensajeGeneral = resultData?.message || 'Errores de validación en datos del plan'
          
          this.$logger.log('📝 Errores de validación del backend:', {
            message: mensajeGeneral,
            errors: erroresEspecificos,
            rawResponse: error.response.data
          })
          
          // Procesar errores específicos y mapearlos a campos del formulario
          this.procesarErroresValidacionBackend(erroresEspecificos, mensajeGeneral)
          
          // Mostrar modal con errores específicos
          this.mostrarErroresValidacionModal(erroresEspecificos, mensajeGeneral)
          
          // No seguir con el flujo normal de error - ya se mostró el modal específico
          return
        } else if (error.response?.status === 401) {
          errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
          errorTitle = 'Sesión Expirada'
          setTimeout(function() {
            sessionStorage.clear()
            this.$router.push('/login')
          }.bind(this), 2000)
        } else if (error.message) {
          errorMessage = error.message
        }

        this.$swal.fire({
          title: errorTitle,
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-danger'
        })
      } finally {
        this.loading.guardar = false
      }
    },

    getFamiliaSeleccionadaNombreForm: function() {
      if (!this.formPlan.familia) return 'N/A'
      var familia = this.catalogos.familias.find(function(f) { return f._id === this.formPlan.familia }.bind(this))
      return familia ? familia.nombre : 'N/A'
    },

    getSubFamiliaSeleccionadaNombreForm: function() {
      if (!this.formPlan.subfamilia) return 'N/A'
      var subfamilia = this.subfamiliasFormulario.find(function(sf) { return sf._id === this.formPlan.subfamilia }.bind(this))
      return subfamilia ? subfamilia.nombre : 'N/A'
    },

    seleccionarTurno: function(turno) {
      if (this.loading.guardar) return
      
      this.formPlan.turnoSeleccionado = turno._id
      
      this.$swal.fire({
        title: 'Turno Seleccionado',
        text: 'Has seleccionado el turno "' + turno.descripcionTurno + '"',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      })
    },

    formatearHora: function(horaString) {
      if (!horaString) return 'N/A'
      
      // Convertir formato HH:MM:SS a HH:MM
      var partes = horaString.split(':')
      if (partes.length >= 2) {
        return partes[0] + ':' + partes[1]
      }
      
      return horaString
    },

    limpiarNavegacion: function() {
      this.$logger.log('🧹 Iniciando limpieza de navegación y filtros')
      
      // Limpiar la categoría preseleccionada (esto se debe hacer en el componente padre)
      this.$emit('limpiarCategoria')
      
      // Limpiar filtros locales explícitamente
      this.filtros.idFamilia = ''
      this.filtros.idSubFamilia = ''
      
      // Resetear paginación explícitamente
      this.pagination.page = 1
      
      // Recargar datos con filtros limpios
      this.$nextTick(async () => {
        await this.cargarPlanes()
        
        this.$logger.log('✅ Navegación y filtros limpiados, cargando todos los planes')
        
        this.$swal.fire({
          title: 'Filtros Limpiados',
          text: 'Ahora puedes ver todos los planes del sistema',
          icon: 'info',
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        })
      })
    },

    // Función para formatear precios sin redondeo y sin decimales innecesarios
    formatearPrecioExacto: function(valor) {
      if (valor === null || valor === undefined || isNaN(valor)) {
        return '0'
      }
      
      // Convertir a número para asegurar que es numérico
      const numeroValor = Number(valor)
      
      // Convertir a string para manipular los decimales
      let valorString = numeroValor.toString()
      
      // Si tiene notación científica, expandirla
      if (valorString.includes('e')) {
        valorString = numeroValor.toFixed(10).replace(/\.?0+$/, '')
      }
      
      // Separar parte entera y decimal
      const partes = valorString.split('.')
      const parteEntera = partes[0]
      const parteDecimal = partes[1] || ''
      
      // Formatear la parte entera con separadores de miles
      const parteEnteraFormateada = Number(parteEntera).toLocaleString('es-CL')
      
      // Si no hay decimales o todos son ceros, retornar solo la parte entera
      if (!parteDecimal || parteDecimal === '0' || /^0+$/.test(parteDecimal)) {
        return parteEnteraFormateada
      }
      
      // Remover ceros al final de los decimales
      const decimalSinCeros = parteDecimal.replace(/0+$/, '')
      
      // Retornar con coma como separador decimal (formato chileno)
      return parteEnteraFormateada + ',' + decimalSinCeros
    },

    cargarDatosPlanEnFormulario: function(plan) {
      this.$logger.log('📋 Cargando datos del plan en formulario:', plan)
      
      // Buscar familia y subfamilia por _id
      var familiaId = null
      var subfamiliaId = null
      
      // Buscar familia por nombre (como fallback si no se encuentra por _id)
      var familiaEncontrada = this.catalogos.familias.find(function(f) {
        return f.nombre === plan.nombreFamilia || f.id === plan.idFamilia
      })
      
      if (familiaEncontrada) {
        familiaId = familiaEncontrada._id
        
        // Buscar subfamilia dentro de esa familia
        var subfamiliaEncontrada = this.catalogos.subfamilias.find(function(sf) {
          return sf.nombre === plan.nombreSubFamilia || sf.id === plan.idSubFamilia
        })
        
        if (subfamiliaEncontrada) {
          subfamiliaId = subfamiliaEncontrada._id
        }
      }
      
      // Buscar turno seleccionado
      var turnoSeleccionado = null
      if (plan.turno && plan.turno.idTurno) {
        var turnoEncontrado = this.catalogosCreacion.turnos.find(function(t) {
          return t.id === plan.turno.idTurno
        })
        if (turnoEncontrado) {
          turnoSeleccionado = turnoEncontrado._id
        }
      }
      
      // Convertir fechas del backend (DD-MM-YYYY) al formato HTML (YYYY-MM-DD)
      var fechaInicioConvertida = ''
      var fechaTerminoConvertida = ''
      
      if (plan.fechaInicioVigencia) {
        fechaInicioConvertida = this.convertirFechaBackendAFormulario(plan.fechaInicioVigencia)
        this.$logger.log('📅 Fecha inicio convertida:', plan.fechaInicioVigencia, '→', fechaInicioConvertida)
      }
      
      if (plan.fechaTerminoVigencia) {
        fechaTerminoConvertida = this.convertirFechaBackendAFormulario(plan.fechaTerminoVigencia)
        this.$logger.log('📅 Fecha término convertida:', plan.fechaTerminoVigencia, '→', fechaTerminoConvertida)
      }
      
      // Llenar el formulario con los datos del plan
      this.formPlan = {
        // Básico
        familia: familiaId || '',
        subfamilia: subfamiliaId || '',
        descripcion: plan.descripcion || '',
        valor: plan.valor || 0,
        indicadorMatricula: plan.indicadorMatricula || false,
        valorMatricula: plan.valorMatricula || 0,
        porcentajeDescuento: (plan.porcentajeDescuento || 0) * 100, // Convertir a porcentaje
        
        // Facturación
        indicadorFactura: plan.indicadorFactura || false,
        tipoIVA: plan.tipoIVA || 'AFECTO',
        
        // Duración
        duracion: plan.duracion || 1,
        unidadDuracionPlan: plan.unidadDuracionPlan?._id || '',
        indicadorMesCalendario: plan.indicadorMesCalendario !== false, // Default true
        diasMesNoCalendario: plan.diasMesNoCalendario || 0,
        diasPermitidoCongelar: plan.diasPermitidoCongelar || 0,
        
        // Sesiones
        indicadorUsoSesiones: plan.indicadorUsoSesiones || false,
        sesionesLimite: plan.sesionesLimite || 0,
        descuentoSesionesXDia: plan.descuentoSesionesXDia !== false, // Default true
        
        // Sedes
        multiSede: plan.multiSede || false,
        sucursalesHabilitadas: plan.sucursalesHabilitadas || [],
        
        // Vigencia (fechas convertidas al formato HTML)
        indicadorVigenciaTemporal: plan.indicadorVigenciaTemporal || false,
        fechaInicioVigencia: fechaInicioConvertida,
        fechaTerminoVigencia: fechaTerminoConvertida,
        
        // Renovación
        indicadorPermiteRenovar: plan.indicadorPermiteRenovar !== false, // Default true
        expiracionRenovacion: plan.expiracionRenovacion || 30,
        unidadExpiracionRenovacion: plan.unidadExpiracionRenovacion?._id || '',
        
        // Venta
        habilitarVenta: plan.habilitarVenta !== false, // Default true
        habilitarVentaMobile: plan.habilitarVentaMobile !== false, // Default true
        esSuscripcion: plan.esSuscripcion || false,
        convenio: plan.convenio?._id || '',
        
        // Turno
        turnoSeleccionado: turnoSeleccionado,
        
        // Información adicional
        informacionAdicional: {
          limitarPuntoAcceso: plan.informacionAdicional?.limitarPuntoAcceso || false,
          puntoAccesoPermitidos: plan.informacionAdicional?.puntoAccesoPermitidos || [],
          beneficiosAdicionales: plan.informacionAdicional?.beneficiosAdicionales || []
        }
      }
      
      // Si el plan no permite renovación, forzar valores coherentes
      // (campos visibles pero deshabilitados con expiración 0 + unidad INDEFINIDA)
      if (!this.formPlan.indicadorPermiteRenovar) {
        this.formPlan.expiracionRenovacion = 0
        const unidadIndefinida = this.obtenerUnidadIndefinida()
        if (unidadIndefinida) {
          this.formPlan.unidadExpiracionRenovacion = unidadIndefinida._id
        }
      }

      // Limpiar errores de validación visual al cargar datos
      this.limpiarErroresVisuales()

      this.$logger.log('✅ Formulario cargado con datos del plan:', this.formPlan)
      this.$logger.log('📅 Fechas procesadas:', {
        original_inicio: plan.fechaInicioVigencia,
        original_termino: plan.fechaTerminoVigencia,
        convertida_inicio: fechaInicioConvertida,
        convertida_termino: fechaTerminoConvertida
      })
    },

    // Método para convertir fechas del backend (DD-MM-YYYY) al formato HTML (YYYY-MM-DD)
    convertirFechaBackendAFormulario: function(fechaBackend) {
      if (!fechaBackend) return ''
      
      try {
        // Si ya está en formato YYYY-MM-DD, devolverla tal como está
        if (/^\d{4}-\d{2}-\d{2}$/.test(fechaBackend)) {
          return fechaBackend
        }
        
        // Si está en formato DD-MM-YYYY, convertirla
        if (/^\d{2}-\d{2}-\d{4}$/.test(fechaBackend)) {
          var partes = fechaBackend.split('-')
          var dia = partes[0]
          var mes = partes[1]
          var año = partes[2]
          return año + '-' + mes + '-' + dia
        }
        
        // Si está en formato DD/MM/YYYY, convertirla
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(fechaBackend)) {
          var partes = fechaBackend.split('/')
          var dia = partes[0]
          var mes = partes[1]
          var año = partes[2]
          return año + '-' + mes + '-' + dia
        }
        
        // Intentar parse con Date como fallback
        var fecha = new Date(fechaBackend)
        if (!isNaN(fecha.getTime())) {
          return fecha.toISOString().split('T')[0]
        }
        
        console.warn('⚠️ Formato de fecha no reconocido:', fechaBackend)
        return ''
      } catch (error) {
        this.$logger.error('❌ Error al convertir fecha:', fechaBackend, error)
        return ''
      }
    },

    // Método para convertir fechas del formulario (YYYY-MM-DD) al formato backend (DD-MM-YYYY)
    convertirFechaFormularioABackend: function(fechaFormulario) {
      if (!fechaFormulario) return ''
      
      try {
        // Si está en formato YYYY-MM-DD, convertirla a DD-MM-YYYY
        if (/^\d{4}-\d{2}-\d{2}$/.test(fechaFormulario)) {
          var partes = fechaFormulario.split('-')
          var año = partes[0]
          var mes = partes[1]
          var dia = partes[2]
          return dia + '-' + mes + '-' + año
        }
        
        console.warn('⚠️ Formato de fecha de formulario no reconocido:', fechaFormulario)
        return fechaFormulario
      } catch (error) {
        this.$logger.error('❌ Error al convertir fecha de formulario:', fechaFormulario, error)
        return fechaFormulario
      }
    },

    // Método para limpiar errores visuales del formulario
    limpiarErroresVisuales: function() {
      this.erroresValidacion = {}
      
      // Remover clases de error de todos los campos
      var self = this
      this.$nextTick(function() {
        var camposConError = document.querySelectorAll('.form-control.is-invalid')
        camposConError.forEach(function(campo) {
          campo.classList.remove('is-invalid')
        })
        
        var mensajesError = document.querySelectorAll('.invalid-feedback')
        mensajesError.forEach(function(mensaje) {
          mensaje.remove()
        })
      })
    },

    // Método para procesar errores de validación del backend y mapearlos a campos
    procesarErroresValidacionBackend: function(erroresArray, mensajeGeneral) {
      // Limpiar errores previos
      this.limpiarErroresVisuales()
      
      // Mapeo de errores del backend a campos del formulario
      var mapeoErrores = {
        'familia': ['familia'],
        'subfamilia': ['subfamilia'],
        'descripcion': ['descripcion', 'descripcionPlan'],
        'valor': ['valor'],
        'valorMatricula': ['valorMatricula'],
        'duracion': ['duracion'],
        'unidadDuracionPlan': ['unidadDuracionPlan'],
        'convenio': ['convenio'],
        'fechaInicioVigencia': ['fechaInicioVigencia'],
        'fechaTerminoVigencia': ['fechaTerminoVigencia'],
        'sesionesLimite': ['sesionesLimite'],
        'expiracionRenovacion': ['expiracionRenovacion'],
        'turno': ['turnoSeleccionado']
      }
      
      var erroresEncontrados = {}
      
      // Procesar cada error del array
      erroresArray.forEach(function(error) {
        var mensajeError = error
        var campoAfectado = null
        
        // Detectar el campo afectado basado en el mensaje de error
        Object.keys(mapeoErrores).forEach(function(campo) {
          if (mensajeError.toLowerCase().includes(campo.toLowerCase()) || 
              mensajeError.toLowerCase().includes(campo)) {
            campoAfectado = campo
          }
        })
        
        // Casos específicos para fechas
        if (mensajeError.toLowerCase().includes('fecha')) {
          if (mensajeError.toLowerCase().includes('inicio')) {
            campoAfectado = 'fechaInicioVigencia'
          } else if (mensajeError.toLowerCase().includes('termino') || mensajeError.toLowerCase().includes('término')) {
            campoAfectado = 'fechaTerminoVigencia'
          } else if (mensajeError.toLowerCase().includes('vigencia')) {
            // Si no especifica cuál fecha, marcar ambas
            erroresEncontrados['fechaInicioVigencia'] = mensajeError
            erroresEncontrados['fechaTerminoVigencia'] = mensajeError
            return
          }
        }
        
        if (campoAfectado) {
          erroresEncontrados[campoAfectado] = mensajeError
        } else {
          // Error general sin campo específico
          console.warn('⚠️ Error sin campo específico detectado:', mensajeError)
        }
      })
      
      // Guardar errores en el estado del componente
      this.erroresValidacion = erroresEncontrados
      
      // Aplicar estilos visuales a los campos con errores
      this.$nextTick(function() {
        this.aplicarErroresVisualesFormulario(erroresEncontrados)
      }.bind(this))
      
      this.$logger.log('📝 Errores procesados para campos:', erroresEncontrados)
    },

    // Método para aplicar estilos visuales de error a los campos del formulario
    aplicarErroresVisualesFormulario: function(erroresEncontrados) {
      var mapeoIdsCampos = {
        'familia': 'familia',
        'subfamilia': 'subfamilia', 
        'descripcion': 'descripcionPlan',
        'valor': 'valor',
        'valorMatricula': 'valorMatricula',
        'duracion': 'duracion',
        'unidadDuracionPlan': 'unidadDuracionPlan',
        'convenio': 'convenio',
        'fechaInicioVigencia': 'fechaInicioVigencia',
        'fechaTerminoVigencia': 'fechaTerminoVigencia',
        'sesionesLimite': 'sesionesLimite',
        'expiracionRenovacion': 'expiracionRenovacion'
      }
      
      Object.keys(erroresEncontrados).forEach(function(campo) {
        var idCampo = mapeoIdsCampos[campo] || campo
        var elemento = document.getElementById(idCampo) || 
                      document.querySelector('[v-model*="' + campo + '"]') ||
                      document.querySelector('select[v-model*="' + campo + '"]') ||
                      document.querySelector('input[v-model*="' + campo + '"]')
        
        if (elemento) {
          // Agregar clase de error
          elemento.classList.add('is-invalid')
          
          // Crear mensaje de error si no existe
          var mensajeError = erroresEncontrados[campo]
          var contenedorError = elemento.parentNode.querySelector('.invalid-feedback')
          
          if (!contenedorError) {
            contenedorError = document.createElement('div')
            contenedorError.className = 'invalid-feedback'
            contenedorError.style.display = 'block'
            elemento.parentNode.appendChild(contenedorError)
          }
          
          contenedorError.textContent = mensajeError
          
          this.$logger.log('🎨 Campo marcado con error:', idCampo, mensajeError)
        } else {
          console.warn('⚠️ No se encontró elemento para campo:', campo)
        }
      })
    },

    // Método para mostrar modal con errores de validación específicos
    mostrarErroresValidacionModal: function(erroresArray, mensajeGeneral) {
      var erroresHTML = ''
      
      if (erroresArray && erroresArray.length > 0) {
        erroresHTML = '<ul class="text-left" style="margin: 0; padding-left: 20px;">'
        erroresArray.forEach(function(error) {
          erroresHTML += '<li style="margin-bottom: 5px; color: #dc3545;"><strong>' + error + '</strong></li>'
        })
        erroresHTML += '</ul>'
      } else {
        erroresHTML = '<p class="text-center">' + mensajeGeneral + '</p>'
      }
      
      this.$swal.fire({
        title: 'Errores de Validación',
        html: `
          <div class="text-center">
            <i class="nc-icon nc-simple-remove" style="font-size: 3rem; color: #dc3545; margin-bottom: 15px;"></i>
            <h5 style="color: #dc3545; margin-bottom: 15px;">¡Datos Incorrectos!</h5>
            
            <div class="alert alert-danger text-left" style="margin: 15px 0;">
              <strong>❌ Se encontraron los siguientes errores:</strong>
              <div style="margin-top: 10px;">
                ${erroresHTML}
              </div>
            </div>
            
            <div style="margin-top: 15px; padding: 10px; background: rgba(220, 53, 69, 0.1); border-radius: 4px;">
              <small class="text-muted">
                <strong>🔍 Los campos con errores han sido marcados en rojo.</strong><br>
                Corrige los datos indicados y vuelve a intentar guardar el plan.
              </small>
            </div>
          </div>
        `,
        icon: 'error',
        confirmButtonText: 'Corregir Datos',
        confirmButtonClass: 'btn btn-danger',
        allowOutsideClick: true,
        allowEscapeKey: true,
        customClass: {
          container: 'swal-validacion-errores'
        }
      }).then(function() {
        // Enfocar el primer campo con error
        var primerCampoConError = document.querySelector('.form-control.is-invalid')
        if (primerCampoConError) {
          primerCampoConError.focus()
        }
      })
    },

    obtenerUnidadIndefinida: function() {
      if (!this.catalogosCreacion || !Array.isArray(this.catalogosCreacion.unidadesExpiracionRenovacion)) {
        return null
      }
      return this.catalogosCreacion.unidadesExpiracionRenovacion.find(function(unidad) {
        return unidad && typeof unidad.glosa === 'string' && unidad.glosa.toUpperCase().includes('INDEFINIDA')
      })
    },

    obtenerUnidadPorId: function(id) {
      return this.catalogosCreacion.unidadesExpiracionRenovacion.find(function(unidad) {
        return unidad._id === id
      })
    },

    esUnidadIndefinida: function(unidad) {
      return !!(unidad && typeof unidad.glosa === 'string' && unidad.glosa.toUpperCase().includes('INDEFINIDA'))
    },

    obtenerPrimeraUnidadValida: function() {
      return this.catalogosCreacion.unidadesExpiracionRenovacion.find(function(unidad) {
        return !this.esUnidadIndefinida(unidad)
      }.bind(this))
    }
  }
}
</script>

<style scoped>
.planes-manager {
  padding: 20px;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.manager-header {
  background: white;
  padding: 25px;
  border-radius: 12px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.manager-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
}

.categoria-preseleccionada .card {
  border: 2px solid #28a745;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.1);
}

.categoria-info {
  /* Contenedor verde removido según solicitud del usuario */
}

.filters-section .card,
.table-section .card {
  border: none;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.form-label {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
  font-size: 0.9rem;
}

.btn-sort {
  background: none;
  border: none;
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0;
  text-align: left;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.btn-sort:hover {
  color: #667eea;
}

.empty-icon {
  font-size: 4rem;
  color: #dee2e6;
}

.empty-state {
  padding: 3rem 1rem;
}

.pagination-section .pagination {
  margin-bottom: 0;
}

.pagination-section .page-link {
  border-radius: 6px;
  margin: 0 2px;
  border: 1px solid #dee2e6;
}

  .pagination-section .page-item.active .page-link {
    background-color: #667eea;
    border-color: #667eea;
  }

  /* Estilos para errores de validación */
  .form-control.is-invalid {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
    background-image: none;
  }

  .form-control.is-invalid:focus {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
  }

  .invalid-feedback {
    display: block !important;
    width: 100%;
    margin-top: 0.25rem;
    font-size: 0.875rem;
    color: #dc3545;
    font-weight: 500;
    background: rgba(220, 53, 69, 0.1);
    padding: 6px 12px;
    border-radius: 4px;
    border-left: 3px solid #dc3545;
  }

  /* Estilos para modal de errores de validación */
  :deep(.swal-validacion-errores) .swal2-popup {
    border-radius: 15px;
    padding: 2rem;
    max-width: 600px;
  }

  :deep(.swal-validacion-errores) .swal2-title {
    color: #dc3545;
    font-weight: 700;
  }

  :deep(.swal-validacion-errores) .swal2-html-container {
    margin: 1.5rem 0;
    text-align: left;
  }

  :deep(.swal-validacion-errores) .alert-danger {
    border-color: #dc3545;
    background-color: rgba(220, 53, 69, 0.1);
    margin: 15px 0;
  }

  :deep(.swal-validacion-errores) .swal2-confirm {
    background: #dc3545 !important;
    border: none !important;
    border-radius: 8px !important;
    padding: 10px 25px !important;
    font-weight: 600 !important;
  }

.badge-outline-warning {
  color: #ffc107;
  border: 1px solid #ffc107;
  background-color: transparent;
}

.plan-info strong {
  color: #2c3e50;
}

.plan-nombre-clickeable {
  cursor: pointer;
  color: #667eea;
  transition: color 0.3s ease;
}

.plan-nombre-clickeable:hover {
  color: #764ba2;
  text-decoration: underline;
}

.precio-info strong {
  font-size: 1.1rem;
}

.categoria-info strong {
  color: #667eea;
}

.caracteristicas-info .badge-sm {
  font-size: 0.7rem;
  padding: 0.25rem 0.4rem;
}

.filtros-activos .badge {
  cursor: default;
}

.filtros-activos .badge i {
  opacity: 0.7;
}

.filtros-activos .badge i:hover {
  opacity: 1;
}

/* Estilos para modal de creación */
.modal-xl {
  max-width: 90%;
}

/* Altura fija para evitar cambios de tamaño al cambiar pestañas */
.modal-xl .modal-dialog {
  display: flex;
  align-items: center;
  min-height: calc(100vh - 60px);
}

.modal-xl .modal-content {
  border-radius: 12px;
  border: none;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
  height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-xl .modal-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden;
}

/* Header compacto y elegante */
.modal-xl .modal-header {
  padding: 12px 20px;
  border-bottom: 1px solid #e9ecef;
  border-radius: 12px 12px 0 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  flex-shrink: 0;
}

.modal-xl .modal-title {
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.2;
}

.modal-xl .modal-header .close {
  color: white;
  opacity: 0.8;
  padding: 0;
  margin: -12px -20px -12px auto;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-xl .modal-header .close:hover {
  opacity: 1;
  background: rgba(255,255,255,0.1);
}

/* Contenedor sticky para las pestañas */
.sticky-tabs-container {
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
  padding: 12px 12px 0;
  border-bottom: 1px solid #e9ecef;
}

/* Contenedor scrollable para el contenido */
.tab-content-scrollable {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.tab-content-inner {
  padding: 20px;
}

/* Evitar scroll horizontal */
.modal-xl .tab-pane {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.modal-xl .form-row,
.modal-xl .row {
  margin-left: -15px;
  margin-right: -15px;
}

.modal-xl .col,
.modal-xl [class*="col-"] {
  padding-left: 15px;
  padding-right: 15px;
}

.nav-tabs {
  border: none;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 8px;
}

.nav-tabs .nav-link {
  border: none;
  border-radius: 6px;
  color: #6c757d;
  font-weight: 600;
  padding: 12px 20px;
  margin: 0 4px;
  transition: all 0.3s ease;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Espaciado correcto para íconos en escritorio */
.nav-tabs .nav-link i {
  margin-right: 8px !important;
}

.nav-tabs .nav-link:hover {
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
}

.nav-tabs .nav-link.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 15px 0 rgba(102, 126, 234, 0.4);
}

/* Layout móvil - pestañas en grupos de 3 */
@media (max-width: 768px) {
  .modal-xl .modal-dialog {
    margin: 0.5rem;
  }
  
  .modal-xl .modal-content {
    height: calc(100vh - 1rem);
  }
  
  .modal-xl .modal-header {
    padding: 10px 15px;
  }
  
  .modal-xl .modal-title {
    font-size: 1rem;
  }
  
  .sticky-tabs-container {
    padding: 8px 8px 0;
  }
  
  .nav-tabs {
    display: flex;
    flex-wrap: wrap;
    padding: 4px;
  }
  
  .nav-tabs .nav-item {
    flex: 0 0 33.333%;
    max-width: 33.333%;
    padding: 2px;
  }
  
  .nav-tabs .nav-link {
    padding: 8px 4px;
    margin: 0;
    font-size: 0.8rem;
    flex-direction: column;
  }
  
  .nav-tabs .nav-link i {
    margin-right: 0 !important;
    margin-bottom: 4px;
    font-size: 1.2rem;
  }
  
  .tab-content-inner {
    padding: 15px;
  }
  
  .modal-xl .modal-footer {
    padding: 10px 15px;
  }
}

.form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

/* Footer del modal */
.modal-xl .modal-footer {
  padding: 12px 20px;
  border-top: 1px solid #e9ecef;
  flex-shrink: 0;
}

.custom-control-input:checked ~ .custom-control-label::before {
  background-color: #667eea;
  border-color: #667eea;
}

.custom-control-input:focus ~ .custom-control-label::before {
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.tab-pane {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Estilos para selección de turnos */
.turnos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.turno-card {
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  position: relative;
  overflow: hidden;
}

.turno-card:hover {
  border-color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
}

.turno-card.selected {
  border-color: #28a745;
  background: linear-gradient(135deg, #f8fff9 0%, #e8f5e8 100%);
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.2);
}

.turno-title {
  color: #2c3e50;
  font-weight: 600;
  font-size: 1.1rem;
}

.turno-id .badge {
  font-size: 0.75rem;
}

.dias-semana {
  display: flex;
  gap: 4px;
  margin-bottom: 5px;
}

.dia-badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

.dia-badge.activo {
  background: #28a745;
  color: white;
  border-color: #28a745;
}

.turno-horarios {
  font-size: 0.9rem;
}

.horario-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 2px;
}

.horario-dia {
  font-weight: 600;
  color: #495057;
  min-width: 35px;
}

.horario-tiempo {
  color: #6c757d;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
}

.turno-selected {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #28a745;
  color: white;
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}

.turno-selected i {
  font-size: 0.8rem;
}

.turno-resumen .card {
  border: 2px solid #28a745;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.1);
}

.turno-resumen .card-header {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border-bottom: none;
}

.turno-resumen .table th {
  border-top: none;
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.turno-resumen .table td {
  font-size: 0.85rem;
  font-family: 'Courier New', monospace;
}

/* Estilos para el modal de detalle profesional */
:deep(.plan-detalle-modal) .swal2-popup {
  border-radius: 15px;
  padding: 0;
  overflow: hidden;
  max-width: 1000px;
  background: #ffffff;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e6ed;
}

:deep(.plan-detalle-modal) .swal2-title {
  display: none;
}

:deep(.plan-detalle-modal) .swal2-html-container {
  margin: 0;
  padding: 0;
  overflow-y: auto;
  max-height: 75vh;
}

:deep(.plan-detalle-modal) .swal2-actions {
  margin-top: 0;
  padding: 20px 30px;
  border-top: 1px solid #e0e6ed;
  background: #f8f9fa;
}

:deep(.plan-detalle-modal) .swal2-confirm {
  background: #6c757d !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 10px 20px !important;
  font-weight: 600 !important;
}

:deep(.plan-detalle-modal) .swal2-cancel {
  background: #667eea !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 10px 20px !important;
  font-weight: 600 !important;
  color: white !important;
}

.plan-detalle-profesional {
  font-family: 'Segoe UI', 'Open Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #2c3e50;
  background: #ffffff;
  min-height: 100%;
}

/* Header del Plan */
.plan-header-pro {
  background: #ffffff;
  border-bottom: 1px solid #e0e6ed;
  padding: 30px;
}

.plan-header-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 25px;
}

.plan-icon-pro {
  width: 70px;
  height: 70px;
  background: #f8f9fa;
  border: 2px solid #e0e6ed;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #495057;
  flex-shrink: 0;
}

.plan-info-pro {
  flex: 1;
  min-width: 0;
}

.plan-title-pro {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 10px;
  line-height: 1.2;
}

.plan-meta-pro {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  align-items: center;
}

.plan-id-pro {
  background: #f8f9fa;
  color: #6c757d;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  border: 1px solid #e0e6ed;
}

.plan-status-pro {
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 5px;
}

.plan-status-pro.active {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.plan-status-pro.inactive {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.plan-price-pro {
  text-align: right;
  flex-shrink: 0;
}

.price-value {
  font-size: 2.5rem;
  font-weight: 800;
  color: #2c3e50;
  margin-bottom: 5px;
  line-height: 1;
}

.price-note {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
}

/* Contenido Principal */
.plan-body-pro {
  padding: 0 30px 30px;
}

/* Secciones */
.section-pro {
  margin-bottom: 30px;
  background: #ffffff;
  border: 1px solid #e0e6ed;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-header-pro {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px 25px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  position: relative;
  overflow: hidden;
}

.section-header-pro::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #495057 0%, #6c757d 100%);
}

.section-header-pro::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  background: radial-gradient(circle, rgba(73, 80, 87, 0.1) 0%, transparent 70%);
  border-radius: 50%;
}

.section-icon-pro {
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, #495057 0%, #6c757d 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(73, 80, 87, 0.25);
  border: 2px solid rgba(255, 255, 255, 0.2);
  position: relative;
}

.section-icon-pro::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  right: 2px;
  height: 50%;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 100%);
  border-radius: 8px;
}

.section-title-pro {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  flex: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.5px;
}

.section-subtitle-pro {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 600;
  font-style: italic;
  background: rgba(108, 117, 125, 0.1);
  padding: 4px 12px;
  border-radius: 15px;
  border: 1px solid rgba(108, 117, 125, 0.2);
  text-shadow: 0 1px 1px rgba(255, 255, 255, 0.8);
}

.section-content-pro {
  padding: 25px;
}

/* Información Básica */
.data-grid-pro {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.data-row-pro {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.data-item-pro {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e6ed;
}

.data-label-pro {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.data-value-pro {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
}

/* Configuración */
.config-cards-pro {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 15px;
}

.config-card-pro {
  background: #f8f9fa;
  border: 1px solid #e0e6ed;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.2s ease;
}

.config-card-pro:hover {
  background: #ffffff;
  border-color: #adb5bd;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.config-icon-pro {
  width: 45px;
  height: 45px;
  background: #495057;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: white;
  flex-shrink: 0;
}

.config-details-pro {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
}

.config-label-pro {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.config-value-pro {
  font-size: 1rem;
  font-weight: 700;
  color: #2c3e50;
}

.config-value-pro.positive {
  color: #28a745;
}

.config-value-pro.negative {
  color: #dc3545;
}

/* Características */
.features-grid-pro {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.feature-item-pro {
  background: #f8f9fa;
  border: 1px solid #e0e6ed;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.feature-item-pro.enabled {
  background: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.feature-item-pro.disabled {
  background: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.feature-item-pro i {
  font-size: 1.1rem;
  flex-shrink: 0;
}

/* Horarios */
.horarios-table-pro {
  background: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e6ed;
}

.horario-row-pro {
  display: grid;
  grid-template-columns: 120px 1fr;
  padding: 15px 20px;
  border-bottom: 1px solid #e0e6ed;
  transition: background-color 0.2s ease;
}

.horario-row-pro:last-child {
  border-bottom: none;
}

.horario-row-pro:hover {
  background: #ffffff;
}

.dia-pro {
  font-weight: 700;
  color: #2c3e50;
  font-size: 0.9rem;
}

.hora-pro {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
  font-family: monospace;
}

/* Información Adicional */
.subsection-pro {
  margin-bottom: 25px;
}

.subsection-pro:last-child {
  margin-bottom: 0;
}

.subsection-title-pro {
  font-size: 1rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 15px;
  padding: 8px 15px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-left: 4px solid #6c757d;
  border-radius: 0 8px 8px 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.subsection-title-pro::before {
  content: '';
  position: absolute;
  left: -4px;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #495057 0%, #6c757d 100%);
  border-radius: 2px 0 0 2px;
}

.access-points-pro {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
}

.access-point-pro {
  background: #f8f9fa;
  border: 1px solid #e0e6ed;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
}

.access-point-pro:hover {
  background: #ffffff;
  border-color: #adb5bd;
}

.point-name-pro {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.point-status-pro {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
}

.point-status-pro.discount {
  background: #d4edda;
  color: #155724;
}

.point-status-pro.no-discount {
  background: #f8d7da;
  color: #721c24;
}

.benefits-pro {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.benefit-item-pro {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #155724;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.benefit-item-pro:hover {
  background: #c3e6cb;
}

.benefit-item-pro i {
  color: #28a745;
  font-size: 1.1rem;
  flex-shrink: 0;
}

/* Metadatos */
.metadata-section-pro {
  border-top: 2px solid #e0e6ed;
}

.metadata-table-pro {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.metadata-row-pro {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 15px;
  padding: 12px 15px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e0e6ed;
  align-items: center;
}

.metadata-label-pro {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metadata-value-pro {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2c3e50;
}

/* Responsive */
@media (max-width: 768px) {
  .planes-manager {
    padding: 10px;
  }
  
  .manager-header {
    padding: 15px;
    text-align: center;
  }
  
  .filters-section .row > div {
    margin-bottom: 15px;
  }
  
  .table-responsive {
    font-size: 0.9rem;
  }
  
  .caracteristicas-info .badge-sm {
    font-size: 0.6rem;
    padding: 0.2rem 0.3rem;
  }
  
  .modal-xl {
    max-width: 95%;
    margin: 10px auto;
  }
  
  /* Este estilo se reemplaza por el nuevo layout móvil de arriba */
  
  .turnos-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .turno-card {
    padding: 15px;
  }
  
  .turno-title {
    font-size: 1rem;
  }
  
  .dias-semana {
    justify-content: center;
  }
  
  .turno-resumen .card-body {
    padding: 15px;
  }
  
  .turno-resumen .row {
    flex-direction: column;
  }
  
  /* Responsive para modal de detalle profesional */
  :deep(.plan-detalle-modal) .swal2-popup {
    max-width: 95%;
    margin: 10px;
  }
  
  .plan-header-main {
    flex-direction: column;
    text-align: center;
    gap: 20px;
  }
  
  .plan-body-pro {
    padding: 0 20px 20px;
  }
  
  .section-header-pro {
    padding: 15px 20px;
  }
  
  .section-content-pro {
    padding: 20px;
  }
  
  .data-row-pro {
    grid-template-columns: 1fr;
  }
  
  .config-cards-pro,
  .features-grid-pro {
    grid-template-columns: 1fr;
  }
  
  .metadata-row-pro {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .access-points-pro,
  .benefits-pro {
    grid-template-columns: 1fr;
  }
}

/* Estilos para Modal de Detalle Profesional */
:deep(.swal-detalle-plan) .swal2-popup {
  border-radius: 15px;
  padding: 0;
  max-width: 1200px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
}

:deep(.swal-detalle-plan) .swal2-title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  margin: 0;
  padding: 20px 30px;
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
  border-radius: 15px 15px 0 0;
}

:deep(.swal-detalle-plan) .swal2-html-container {
  padding: 0;
  margin: 0;
  max-height: calc(90vh - 140px);
  overflow-y: auto;
}

/* Contenedor principal del detalle */
.plan-detalle-container {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #2c3e50;
}

/* Header del plan */
.plan-detalle-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 25px 30px;
  display: flex;
  align-items: center;
  gap: 20px;
  border-bottom: 2px solid #dee2e6;
}

.plan-icon-header {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.8rem;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.plan-info-header {
  flex: 1;
  text-align: center;
}

.plan-precio-principal {
  font-size: 2.5rem;
  font-weight: 700;
  color: #28a745;
  margin-bottom: 5px;
}

.plan-matricula-info {
  font-size: 1rem;
  color: #6c757d;
  margin-bottom: 10px;
}

.plan-estado-badge .badge {
  font-size: 0.9rem;
  padding: 8px 15px;
}

/* Secciones del detalle */
.detalle-seccion {
  margin-bottom: 30px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  border: 1px solid #f0f0f0;
}

.seccion-titulo {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px 30px;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #495057;
}

.seccion-titulo h5 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.seccion-titulo i {
  color: #667eea;
  font-size: 1.3rem;
}

/* Tablas de detalle */
.tabla-detalle {
  padding: 20px 30px;
}

.tabla-detalle .table {
  margin-bottom: 0;
}

.etiqueta-dato {
  font-weight: 600;
  color: #495057;
  width: 25%;
  padding: 12px 8px;
  background-color: rgba(102, 126, 234, 0.05);
}

.valor-dato {
  padding: 12px 8px;
  color: #2c3e50;
  font-weight: 500;
}

/* Grid de configuración */
.configuracion-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  padding: 20px 30px;
}

.config-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
  border-left: 4px solid transparent;
  transition: all 0.3s ease;
}

.config-item:hover {
  background: #e9ecef;
  transform: translateY(-2px);
}

.config-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: bold;
}

.config-icon.enabled {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  border-left-color: #28a745;
}

.config-icon.disabled {
  background: #6c757d;
  color: white;
  border-left-color: #6c757d;
}

.config-item.enabled {
  border-left-color: #28a745;
}

.config-item.disabled {
  border-left-color: #6c757d;
}

.config-texto {
  flex: 1;
}

.config-label {
  display: block;
  font-weight: 600;
  color: #2c3e50;
  font-size: 1rem;
  margin-bottom: 3px;
}

.config-desc {
  display: block;
  color: #6c757d;
  font-size: 0.9rem;
}

/* Información de turno */
.turno-info-detalle {
  padding: 20px 30px;
}

.turno-nombre {
  margin-bottom: 15px;
  padding: 15px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 8px;
  text-align: center;
}

.turno-nombre strong {
  color: #667eea;
  font-size: 1.2rem;
}

.sin-turno {
  text-align: center;
  padding: 40px 30px;
  color: #6c757d;
}

/* Responsive */
@media (max-width: 768px) {
  :deep(.swal-detalle-plan) .swal2-popup {
    width: 95%;
    max-width: 95%;
    margin: 10px;
  }

  :deep(.swal-detalle-plan) .swal2-title {
    padding: 15px 20px;
    font-size: 1.3rem;
  }

  .plan-detalle-header {
    flex-direction: column;
    text-align: center;
    padding: 20px;
  }

  .plan-precio-principal {
    font-size: 2rem;
  }

  .configuracion-grid {
    grid-template-columns: 1fr;
    padding: 15px 20px;
  }

  .tabla-detalle {
    padding: 15px 20px;
  }

  .seccion-titulo {
    padding: 15px 20px;
  }

  .seccion-titulo h5 {
    font-size: 1.1rem;
  }

  .etiqueta-dato,
  .valor-dato {
    width: 100%;
    display: block;
    padding: 8px;
  }

  .etiqueta-dato {
    background-color: rgba(102, 126, 234, 0.1);
    border-bottom: 1px solid #dee2e6;
  }

  .turno-info-detalle {
    padding: 15px 20px;
  }
}

/* Animaciones */
.detalle-seccion {
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.config-item {
  animation: slideInLeft 0.4s ease-out;
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Estilos para modales de eliminación de planes en doble fase */
:deep(.swal-eliminar-plan-etapas) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 600px;
}

:deep(.swal-eliminar-plan-etapas) .swal2-title {
  color: #dc3545;
  font-weight: 700;
}

:deep(.swal-eliminar-plan-etapas) .swal2-html-container {
  margin: 1.5rem 0;
  text-align: left;
}

:deep(.swal-eliminar-plan-etapas) .alert-info {
  border-color: #17a2b8;
  background-color: rgba(23, 162, 184, 0.1);
}

:deep(.swal-eliminar-plan-etapas) .alert-danger {
  border-color: #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
}

:deep(.swal-eliminar-plan-etapas) .alert-warning {
  border-color: #ffc107;
  background-color: rgba(255, 193, 7, 0.1);
}

/* Estilos para modal de eliminación exitosa */
:deep(.swal-eliminacion-plan-exitosa) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 550px;
}

:deep(.swal-eliminacion-plan-exitosa) .swal2-html-container {
  margin: 0;
  padding: 0;
  text-align: left;
}

:deep(.swal-eliminacion-plan-exitosa) .alert-success {
  border-color: #28a745;
  background-color: rgba(40, 167, 69, 0.1);
  margin: 15px 0;
}

:deep(.swal-eliminacion-plan-exitosa) h5 {
  text-align: center;
  margin-bottom: 20px;
}

:deep(.swal-eliminacion-plan-exitosa) .swal2-confirm {
  background: #28a745 !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 10px 25px !important;
  font-weight: 600 !important;
}

/* Estilos para modal de loading de eliminación */
:deep(.swal-loading-eliminar-plan) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 500px;
}

:deep(.swal-loading-eliminar-plan) .swal2-title {
  color: #dc3545;
  font-weight: 700;
  margin-bottom: 20px;
}

:deep(.swal-loading-eliminar-plan) .swal2-html-container {
  margin: 0;
  padding: 0;
}

:deep(.swal-loading-eliminar-plan) .spinner-border {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive para modales de eliminación */
@media (max-width: 768px) {
  :deep(.swal-eliminar-plan-etapas) .swal2-popup,
  :deep(.swal-eliminacion-plan-exitosa) .swal2-popup {
    max-width: 90%;
    margin: 10px;
    padding: 1.5rem;
  }
  
  :deep(.swal-eliminar-plan-etapas) .swal2-html-container ul,
  :deep(.swal-eliminacion-plan-exitosa) .swal2-html-container ul {
    padding-left: 15px;
  }
}

/* ========================================
   ESTILOS PARA LA PESTAÑA DE SUCURSALES
   ======================================== */

/* Grid de sucursales */
.sucursales-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.sucursal-card {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.sucursal-card:hover {
  border-color: #667eea;
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
}

.sucursal-card.selected {
  border-color: #28a745;
  background: rgba(40, 167, 69, 0.05);
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.2);
}

.sucursal-card.fisica {
  border-left: 4px solid #17a2b8;
}

.sucursal-card.virtual {
  border-left: 4px solid #6f42c1;
}

.sucursal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.sucursal-type {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sucursal-type i {
  font-size: 1.2rem;
  color: #667eea;
}

.sucursal-type-text {
  font-size: 0.8rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sucursal-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #28a745;
  border-color: #28a745;
}

.sucursal-content {
  margin-bottom: 15px;
}

.sucursal-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
  font-size: 1rem;
  line-height: 1.3;
}

.sucursal-id {
  font-size: 0.85rem;
}

.sucursal-selected {
  position: absolute;
  bottom: 15px;
  right: 15px;
  display: flex;
  align-items: center;
  gap: 5px;
  color: #28a745;
  font-weight: 600;
  font-size: 0.8rem;
}

.sucursal-selected i {
  font-size: 1rem;
}

/* Resumen de sucursales seleccionadas */
.sucursales-seleccionadas-lista {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sucursal-seleccionada-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: rgba(40, 167, 69, 0.05);
  border-radius: 8px;
  border-left: 3px solid #28a745;
}

.sucursal-seleccionada-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sucursal-seleccionada-info i {
  color: #28a745;
  font-size: 1.1rem;
}

.sucursal-seleccionada-nombre {
  font-weight: 600;
  color: #2c3e50;
}

/* Controles de sucursales */
.sucursales-controls {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

/* Responsive para sucursales */
@media (max-width: 768px) {
  .sucursales-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .sucursal-card {
    padding: 15px;
  }
  
  .sucursal-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .sucursal-selected {
    position: static;
    justify-content: center;
    margin-top: 10px;
  }
  
  .sucursales-controls .row {
    text-align: center;
  }
  
  .sucursales-controls .col-md-6:first-child {
    margin-bottom: 10px;
  }
}

/* Estilos para modal de funcionalidad en desarrollo */
:deep(.swal-funcionalidad-desarrollo) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 600px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.15);
}

:deep(.swal-funcionalidad-desarrollo) .swal2-title {
  color: #667eea;
  font-weight: 700;
  margin-bottom: 20px;
  font-size: 1.5rem;
}

:deep(.swal-funcionalidad-desarrollo) .swal2-html-container {
  margin: 0;
  padding: 0;
  text-align: left;
}

:deep(.swal-funcionalidad-desarrollo) h5 {
  text-align: center;
  margin-bottom: 20px;
  font-weight: 700;
}

:deep(.swal-funcionalidad-desarrollo) .swal2-confirm {
  background: #667eea !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 12px 30px !important;
  font-weight: 600 !important;
  transition: all 0.3s ease !important;
  font-size: 1rem !important;
}

:deep(.swal-funcionalidad-desarrollo) .swal2-confirm:hover {
  background: #5a6fd8 !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

:deep(.swal-funcionalidad-desarrollo) .swal2-icon.swal2-info {
  border-color: #667eea !important;
  color: #667eea !important;
}

/* Responsive para el modal de desarrollo */
@media (max-width: 768px) {
  :deep(.swal-funcionalidad-desarrollo) .swal2-popup {
    max-width: 95%;
    margin: 10px;
    padding: 1.5rem;
  }
  
  :deep(.swal-funcionalidad-desarrollo) .swal2-title {
    font-size: 1.3rem;
  }
  
  :deep(.swal-funcionalidad-desarrollo) h5 {
    font-size: 1.1rem;
  }
}
</style>

<style>
/*
  Estilo GLOBAL (no scoped) — necesario porque SweetAlert2 monta su contenedor
  fuera del árbol del componente (en <body>), por lo que un selector scoped
  no lo alcanzaría.

  Asegura que cualquier modal, toast, loading o notificación de SweetAlert2
  disparado desde PlanesManager.vue se renderice SIEMPRE por encima del
  modal de Crear/Editar Plan (Bootstrap modal usa z-index 1050-1060).

  Se aplica vía customClass.container = 'swal-sobre-modal-plan' configurado
  en el mixin de this.$swal en el hook created().
*/
.swal2-container.swal-sobre-modal-plan {
  z-index: 20000 !important;
}
</style>
